import type { Candle } from '../types';

const DEFAULT_RSI_LENGTH = 12;
const DEFAULT_STOCH_LENGTH = 14;
const DEFAULT_K_SMOOTHING = 1;
const DEFAULT_D_SMOOTHING = 3;
const DEFAULT_BOLLINGER_LENGTH = 20;
const DEFAULT_BOLLINGER_MULTIPLIER = 2;
const DEFAULT_ATR_LENGTH = 14;
const DEFAULT_BREAKOUT_VOLUME_EMA_LENGTH = 20;
const DEFAULT_USE_BREAKOUT_VOLUME_FILTER = true;
const DEFAULT_BREAKOUT_VOLUME_MULTIPLIER = 1.4;
const DEFAULT_PIVOT_LEFT = 2;
const DEFAULT_PIVOT_RIGHT = 2;

export type ColorProSignalCode = 'B' | 'C' | 'R' | 'H';
export type ColorProSignalDirection = 'bullish' | 'bearish';

export type ColorProCandleState =
  | 'bullishBreakout'
  | 'bullishUnconfirmedReversal'
  | 'bullishTrendWeak'
  | 'bullishTrendStrong'
  | 'bearishBreakdown'
  | 'bearishPivot'
  | 'bearishTrend'
  | 'stalledAfterBreakout'
  | 'neutralPivotMinorBullishBias'
  | 'neutralNoTrend';

export interface ColorProSignal {
  index: number;
  code: ColorProSignalCode;
  direction: ColorProSignalDirection;
  price: number;
}

export interface ColorProTradingSuiteV3OverlayResult {
  candleStates: ColorProCandleState[];
  candleColors: (string | undefined)[];
  bollingerUpper: (number | undefined)[];
  bollingerBasis: (number | undefined)[];
  bollingerLower: (number | undefined)[];
  dailyResistance: (number | undefined)[];
  dailySupport: (number | undefined)[];
  weeklyResistance: (number | undefined)[];
  weeklySupport: (number | undefined)[];
  pivotPoints: (number | undefined)[];
  signals: ColorProSignal[];
  info: string;
}

interface ColorProTradingSuiteV3Params {
  candles: Candle[];
  rsiLength?: number;
  stochLength?: number;
  kSmoothing?: number;
  dSmoothing?: number;
  bollingerLength?: number;
  bollingerMultiplier?: number;
  useBreakoutVolumeFilter?: boolean;
  breakoutVolumeMultiplier?: number;
}

interface Pivot {
  index: number;
  value: number;
}

interface PeriodLevels {
  resistance: (number | undefined)[];
  support: (number | undefined)[];
  pivot: (number | undefined)[];
}

export const COLORPRO_CANDLE_COLORS: Record<ColorProCandleState, string> = {
  bullishBreakout: '#4e6cff',
  bullishUnconfirmedReversal: '#f2e83a',
  bullishTrendWeak: '#9dc273',
  bullishTrendStrong: '#7cf146',
  bearishBreakdown: '#7e1f8f',
  bearishPivot: '#eb8c1d',
  bearishTrend: '#d77270',
  stalledAfterBreakout: '#8f8b1f',
  neutralPivotMinorBullishBias: '#d34ae8',
  neutralNoTrend: '#9ca7a9',
};

function ratio(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return 0;
  }
  return numerator / denominator;
}

function bodyRatio(candle: Candle): number {
  return ratio(Math.abs(candle.close - candle.open), Math.max(1e-9, candle.high - candle.low));
}

function lowestNumberInRange(values: number[], start: number, end: number): number {
  let min = Number.POSITIVE_INFINITY;
  for (let index = start; index <= end; index += 1) {
    if (values[index] < min) {
      min = values[index];
    }
  }
  return min;
}

function highestNumberInRange(values: number[], start: number, end: number): number {
  let max = Number.NEGATIVE_INFINITY;
  for (let index = start; index <= end; index += 1) {
    if (values[index] > max) {
      max = values[index];
    }
  }
  return max;
}

function lowestOptionalInRange(
  values: (number | undefined)[],
  start: number,
  end: number,
): number | undefined {
  let min = Number.POSITIVE_INFINITY;
  let seen = false;
  for (let index = start; index <= end; index += 1) {
    const value = values[index];
    if (value === undefined) {
      continue;
    }
    if (value < min) {
      min = value;
    }
    seen = true;
  }
  return seen ? min : undefined;
}

function highestOptionalInRange(
  values: (number | undefined)[],
  start: number,
  end: number,
): number | undefined {
  let max = Number.NEGATIVE_INFINITY;
  let seen = false;
  for (let index = start; index <= end; index += 1) {
    const value = values[index];
    if (value === undefined) {
      continue;
    }
    if (value > max) {
      max = value;
    }
    seen = true;
  }
  return seen ? max : undefined;
}

function smaSeries(values: (number | undefined)[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (length <= 0) {
    return output;
  }

  let sum = 0;
  let valid = 0;
  for (let index = 0; index < values.length; index += 1) {
    const current = values[index];
    if (current !== undefined) {
      sum += current;
      valid += 1;
    }

    if (index >= length) {
      const dropped = values[index - length];
      if (dropped !== undefined) {
        sum -= dropped;
        valid -= 1;
      }
    }

    if (index >= length - 1 && valid === length) {
      output[index] = sum / length;
    }
  }

  return output;
}

function emaSeries(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (!values.length || length <= 0) {
    return output;
  }

  const alpha = 2 / (length + 1);
  let ema = values[0];

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (index === 0) {
      ema = value;
    } else {
      ema = ema + alpha * (value - ema);
    }
    output[index] = ema;
  }

  return output;
}

function atrSeries(candles: Candle[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(candles.length).fill(undefined);
  if (!candles.length || length <= 0) {
    return output;
  }

  const trueRanges: number[] = new Array(candles.length).fill(0);
  trueRanges[0] = candles[0].high - candles[0].low;

  for (let index = 1; index < candles.length; index += 1) {
    const current = candles[index];
    const previousClose = candles[index - 1].close;
    const range = current.high - current.low;
    const highGap = Math.abs(current.high - previousClose);
    const lowGap = Math.abs(current.low - previousClose);
    trueRanges[index] = Math.max(range, highGap, lowGap);
  }

  if (candles.length < length) {
    return output;
  }

  let sum = 0;
  for (let index = 0; index < length; index += 1) {
    sum += trueRanges[index];
  }

  let atr = sum / length;
  output[length - 1] = atr;

  for (let index = length; index < candles.length; index += 1) {
    atr = (atr * (length - 1) + trueRanges[index]) / length;
    output[index] = atr;
  }

  return output;
}

function rsiSeries(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (values.length <= length || length <= 0) {
    return output;
  }

  let gainSum = 0;
  let lossSum = 0;
  for (let index = 1; index <= length; index += 1) {
    const delta = values[index] - values[index - 1];
    if (delta >= 0) {
      gainSum += delta;
    } else {
      lossSum += -delta;
    }
  }

  let avgGain = gainSum / length;
  let avgLoss = lossSum / length;
  output[length] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  for (let index = length + 1; index < values.length; index += 1) {
    const delta = values[index] - values[index - 1];
    const gain = delta > 0 ? delta : 0;
    const loss = delta < 0 ? -delta : 0;

    avgGain = (avgGain * (length - 1) + gain) / length;
    avgLoss = (avgLoss * (length - 1) + loss) / length;
    output[index] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  return output;
}

function stochRsiSeries(
  closes: number[],
  rsiLength: number,
  stochLength: number,
  kSmoothing: number,
  dSmoothing: number,
): { k: (number | undefined)[]; d: (number | undefined)[] } {
  const rsi = rsiSeries(closes, rsiLength);
  const raw: (number | undefined)[] = new Array(closes.length).fill(undefined);

  for (let index = 0; index < closes.length; index += 1) {
    if (index < stochLength - 1) {
      continue;
    }
    const low = lowestOptionalInRange(rsi, index - stochLength + 1, index);
    const high = highestOptionalInRange(rsi, index - stochLength + 1, index);
    const current = rsi[index];
    if (low === undefined || high === undefined || current === undefined) {
      continue;
    }

    const span = high - low;
    raw[index] = span === 0 ? 50 : ((current - low) / span) * 100;
  }

  const k = smaSeries(raw, Math.max(1, kSmoothing));
  const d = smaSeries(k, Math.max(1, dSmoothing));
  return { k, d };
}

function bollingerBands(
  closes: number[],
  length: number,
  multiplier: number,
): {
  upper: (number | undefined)[];
  basis: (number | undefined)[];
  lower: (number | undefined)[];
} {
  const basis = smaSeries(closes, length);
  const upper: (number | undefined)[] = new Array(closes.length).fill(undefined);
  const lower: (number | undefined)[] = new Array(closes.length).fill(undefined);

  for (let index = 0; index < closes.length; index += 1) {
    const mean = basis[index];
    if (index < length - 1 || mean === undefined) {
      continue;
    }

    let variance = 0;
    for (let bar = index - length + 1; bar <= index; bar += 1) {
      const delta = closes[bar] - mean;
      variance += delta * delta;
    }

    const stdDev = Math.sqrt(variance / length);
    upper[index] = mean + multiplier * stdDev;
    lower[index] = mean - multiplier * stdDev;
  }

  return { upper, basis, lower };
}

function isPivotLow(values: number[], index: number, left: number, right: number): boolean {
  const center = values[index];
  for (let offset = 1; offset <= left; offset += 1) {
    if (center >= values[index - offset]) {
      return false;
    }
  }
  for (let offset = 1; offset <= right; offset += 1) {
    if (center > values[index + offset]) {
      return false;
    }
  }
  return true;
}

function isPivotHigh(values: number[], index: number, left: number, right: number): boolean {
  const center = values[index];
  for (let offset = 1; offset <= left; offset += 1) {
    if (center <= values[index - offset]) {
      return false;
    }
  }
  for (let offset = 1; offset <= right; offset += 1) {
    if (center < values[index + offset]) {
      return false;
    }
  }
  return true;
}

function collectPivots(
  values: number[],
  left: number,
  right: number,
  mode: 'high' | 'low',
): Pivot[] {
  const pivots: Pivot[] = [];
  for (let index = left; index < values.length - right; index += 1) {
    const matches =
      mode === 'high'
        ? isPivotHigh(values, index, left, right)
        : isPivotLow(values, index, left, right);
    if (!matches) {
      continue;
    }

    const value = values[index];
    if (!Number.isFinite(value)) {
      continue;
    }
    pivots.push({ index, value });
  }
  return pivots;
}

function buildRegularAndHiddenDivergences(
  highs: number[],
  lows: number[],
  stochK: (number | undefined)[],
  left: number,
  right: number,
): ColorProSignal[] {
  const signals: ColorProSignal[] = [];
  const lowPivots = collectPivots(lows, left, right, 'low');
  const highPivots = collectPivots(highs, left, right, 'high');
  const minGap = 3;
  const minOscDelta = 2;
  const minPriceDelta = 0.002;

  for (let index = 1; index < lowPivots.length; index += 1) {
    const previous = lowPivots[index - 1];
    const current = lowPivots[index];
    if (current.index - previous.index < minGap) {
      continue;
    }

    const prevOsc = stochK[previous.index];
    const curOsc = stochK[current.index];
    if (prevOsc === undefined || curOsc === undefined) {
      continue;
    }

    if (current.value < previous.value * (1 - minPriceDelta) && curOsc > prevOsc + minOscDelta) {
      signals.push({
        index: current.index,
        code: 'R',
        direction: 'bullish',
        price: current.value,
      });
      continue;
    }

    if (current.value > previous.value * (1 + minPriceDelta) && curOsc < prevOsc - minOscDelta) {
      signals.push({
        index: current.index,
        code: 'H',
        direction: 'bullish',
        price: current.value,
      });
    }
  }

  for (let index = 1; index < highPivots.length; index += 1) {
    const previous = highPivots[index - 1];
    const current = highPivots[index];
    if (current.index - previous.index < minGap) {
      continue;
    }

    const prevOsc = stochK[previous.index];
    const curOsc = stochK[current.index];
    if (prevOsc === undefined || curOsc === undefined) {
      continue;
    }

    if (current.value > previous.value * (1 + minPriceDelta) && curOsc < prevOsc - minOscDelta) {
      signals.push({
        index: current.index,
        code: 'R',
        direction: 'bearish',
        price: current.value,
      });
      continue;
    }

    if (current.value < previous.value * (1 - minPriceDelta) && curOsc > prevOsc + minOscDelta) {
      signals.push({
        index: current.index,
        code: 'H',
        direction: 'bearish',
        price: current.value,
      });
    }
  }

  return signals;
}

function buildYellowCandidates(
  candles: Candle[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  stochK: (number | undefined)[],
  bollingerLower: (number | undefined)[],
): Set<number> {
  const yellow = new Set<number>();
  const selloffLookback = 6;
  const lows = candles.map((bar) => bar.low);

  for (let index = 1; index < candles.length; index += 1) {
    const candle = candles[index];
    const previous = candles[index - 1];
    const fast = emaFast[index];
    const slow = emaSlow[index];
    const prevFast = emaFast[index - 1];
    const k = stochK[index];
    const lower = bollingerLower[index];

    let bearishPressureBars = 0;
    for (let bar = Math.max(1, index - 5); bar < index; bar += 1) {
      if (candles[bar].close <= candles[bar - 1].close) {
        bearishPressureBars += 1;
      }
    }

    const downTrendContext =
      (fast !== undefined && slow !== undefined && fast < slow) ||
      (fast !== undefined && candle.close < fast && (prevFast === undefined || fast <= prevFast)) ||
      bearishPressureBars >= 3;
    const buyersShowing = candle.close > candle.open && candle.close > previous.close;
    const size = bodyRatio(candle);
    const reversalBody = size >= 0.28 && size <= 0.74;
    const previousLower = index > 0 ? bollingerLower[index - 1] : undefined;
    const previousLowerTouch =
      previousLower !== undefined && previous.low <= previousLower * 1.01;
    const oversold =
      (k !== undefined && k <= 45 && bearishPressureBars >= 3) ||
      (lower !== undefined && candle.low <= lower * 1.01) ||
      previousLowerTouch;

    const start = Math.max(0, index - selloffLookback);
    const recentLow = lowestNumberInRange(lows, start, index - 1);
    const freshSelloff = candle.low <= recentLow * 1.01;

    const previousBearish = previous.close < previous.open;
    const range = candle.high - candle.low;
    const closeInUpperHalf = range <= 0 ? false : candle.close >= candle.low + range * 0.52;

    if (
      downTrendContext &&
      buyersShowing &&
      reversalBody &&
      oversold &&
      freshSelloff &&
      previousBearish &&
      closeInUpperHalf
    ) {
      yellow.add(index);
    }
  }

  return yellow;
}

function buildBullishPivotHints(
  candles: Candle[],
  lows: number[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  stochK: (number | undefined)[],
  bollingerLower: (number | undefined)[],
  left: number,
  right: number,
): Set<number> {
  const hints = new Set<number>();
  const pivots = collectPivots(lows, left, right, 'low');

  for (const pivot of pivots) {
    const index = pivot.index;
    const candle = candles[index];
    const fast = emaFast[index];
    const slow = emaSlow[index];
    const k = stochK[index];
    const lower = bollingerLower[index];

    const downOrNeutral =
      (fast !== undefined && slow !== undefined && fast <= slow) ||
      (fast !== undefined && candle.close < fast);
    const oversold = (k !== undefined && k <= 30) || (lower !== undefined && candle.low <= lower * 1.002);

    let bearishPressureBars = 0;
    for (let bar = Math.max(1, index - 5); bar <= index; bar += 1) {
      if (candles[bar].close <= candles[bar - 1].close) {
        bearishPressureBars += 1;
      }
    }

    const indecisionBody = bodyRatio(candle) <= 0.5;

    if (downOrNeutral && oversold && bearishPressureBars >= 4 && indecisionBody) {
      hints.add(index);
    }
  }

  return hints;
}

function buildBlueBreakouts(
  candles: Candle[],
  highs: number[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  stochK: (number | undefined)[],
  volumeEma: (number | undefined)[],
  useBreakoutVolumeFilter: boolean,
  breakoutVolumeMultiplier: number,
): Set<number> {
  const breakouts = new Set<number>();
  const lookback = 6;

  for (let index = lookback; index < candles.length; index += 1) {
    const candle = candles[index];
    const body = bodyRatio(candle);
    if (!(candle.close > candle.open && body >= 0.58)) {
      continue;
    }

    let downPressureBars = 0;
    for (let bar = index - lookback; bar < index; bar += 1) {
      const fast = emaFast[bar];
      const slow = emaSlow[bar];
      if (
        (fast !== undefined && slow !== undefined && fast < slow) ||
        (fast !== undefined && candles[bar].close < fast)
      ) {
        downPressureBars += 1;
      }
    }

    const resistance = highestNumberInRange(highs, index - lookback, index - 1);
    const brokeResistance = candle.close > resistance * 1.0005;
    const k = stochK[index];
    const prevK = stochK[index - 1];
    const momentumLift =
      k === undefined || prevK === undefined ? true : k >= 52 && k >= prevK;

    const baselineVolume = volumeEma[index];
    const breakoutVolumeConfirmed =
      !useBreakoutVolumeFilter ||
      baselineVolume === undefined ||
      candle.volume >= baselineVolume * breakoutVolumeMultiplier;

    if (downPressureBars >= 6 && brokeResistance && momentumLift && breakoutVolumeConfirmed) {
      breakouts.add(index);
    }
  }

  return breakouts;
}

function buildPurpleBreakdowns(
  candles: Candle[],
  lows: number[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  stochK: (number | undefined)[],
  volumeEma: (number | undefined)[],
  useBreakoutVolumeFilter: boolean,
  breakoutVolumeMultiplier: number,
): Set<number> {
  const breakdowns = new Set<number>();
  const lookback = 6;

  for (let index = lookback; index < candles.length; index += 1) {
    const candle = candles[index];
    const body = bodyRatio(candle);
    if (!(candle.close < candle.open && body >= 0.58)) {
      continue;
    }

    let upPressureBars = 0;
    for (let bar = index - lookback; bar < index; bar += 1) {
      const fast = emaFast[bar];
      const slow = emaSlow[bar];
      if (
        (fast !== undefined && slow !== undefined && fast > slow) ||
        (fast !== undefined && candles[bar].close > fast)
      ) {
        upPressureBars += 1;
      }
    }

    const support = lowestNumberInRange(lows, index - lookback, index - 1);
    const brokeSupport = candle.close < support * 0.9995;
    const k = stochK[index];
    const prevK = stochK[index - 1];
    const momentumDrop =
      k === undefined || prevK === undefined ? true : k <= 48 && k <= prevK;

    const baselineVolume = volumeEma[index];
    const breakdownVolumeConfirmed =
      !useBreakoutVolumeFilter ||
      baselineVolume === undefined ||
      candle.volume >= baselineVolume * breakoutVolumeMultiplier;

    if (upPressureBars >= 4 && brokeSupport && momentumDrop && breakdownVolumeConfirmed) {
      breakdowns.add(index);
    }
  }

  return breakdowns;
}

function buildBearishPivots(
  candles: Candle[],
  highs: number[],
  stochK: (number | undefined)[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  bollingerUpper: (number | undefined)[],
  left: number,
  right: number,
): ColorProSignal[] {
  const candidates = collectPivots(highs, left, right, 'high');
  const signals: ColorProSignal[] = [];

  for (const pivot of candidates) {
    const pivotIndex = pivot.index;
    if (pivotIndex < 1 || pivotIndex + 1 >= candles.length) {
      continue;
    }

    const candle = candles[pivotIndex];
    const fast = emaFast[pivotIndex];
    const slow = emaSlow[pivotIndex];
    const prevK = stochK[pivotIndex - 1];
    const k = stochK[pivotIndex];
    const upper = bollingerUpper[pivotIndex];

    const upContext =
      (fast !== undefined && slow !== undefined && fast >= slow) ||
      candle.close > candles[pivotIndex - 1].close;
    const overbought = (k !== undefined && k >= 70) || (upper !== undefined && candle.high >= upper * 0.995);
    const momentumSlowing = prevK === undefined || k === undefined ? true : k < prevK;
    const lowOscillatorPivotAllowed =
      k === undefined || k >= 40 || prevK === undefined || prevK <= 45;

    let bearishFollowThrough = 0;
    let closesBelowPivotClose = 0;
    for (let step = 1; step <= 3; step += 1) {
      const follow = candles[pivotIndex + step];
      if (!follow) {
        break;
      }
      if (follow.close < follow.open) {
        bearishFollowThrough += 1;
      }
      if (follow.close < candle.close) {
        closesBelowPivotClose += 1;
      }
    }

    if (
      upContext &&
      overbought &&
      momentumSlowing &&
      lowOscillatorPivotAllowed &&
      bearishFollowThrough >= 2 &&
      closesBelowPivotClose >= 2
    ) {
      signals.push({
        index: Math.min(candles.length - 1, pivotIndex + 1),
        code: 'B',
        direction: 'bearish',
        price: candle.high,
      });
    }
  }

  return signals;
}

function hasIndexInRange(set: Set<number>, start: number, end: number): boolean {
  for (let index = start; index <= end; index += 1) {
    if (set.has(index)) {
      return true;
    }
  }
  return false;
}

function lastIndexInRange(set: Set<number>, start: number, end: number): number | undefined {
  for (let index = end; index >= start; index -= 1) {
    if (set.has(index)) {
      return index;
    }
  }
  return undefined;
}

function buildConfirmedReversals(
  candles: Candle[],
  highs: number[],
  lows: number[],
  stochK: (number | undefined)[],
  stochD: (number | undefined)[],
  yellowCandidates: Set<number>,
  left: number,
  right: number,
): ColorProSignal[] {
  const pivots = collectPivots(lows, left, right, 'low');
  const signals: ColorProSignal[] = [];

  for (const pivot of pivots) {
    for (let confirmIndex = pivot.index + 1; confirmIndex <= pivot.index + 3; confirmIndex += 1) {
      if (confirmIndex >= candles.length || confirmIndex < 2) {
        break;
      }

      if (!hasIndexInRange(yellowCandidates, Math.max(0, confirmIndex - 3), confirmIndex - 1)) {
        continue;
      }

      const confirm = candles[confirmIndex];
      const previousHigh = highestNumberInRange(highs, Math.max(0, confirmIndex - 3), confirmIndex - 1);
      const bullishBreak =
        confirm.close > confirm.open &&
        confirm.close > previousHigh &&
        confirm.close > candles[pivot.index].high;

      const k = stochK[confirmIndex];
      const d = stochD[confirmIndex];
      const prevK = stochK[confirmIndex - 1];
      const prevD = stochD[confirmIndex - 1];
      const oscillatorTurn =
        k !== undefined && d !== undefined && prevK !== undefined && prevD !== undefined && prevK <= prevD && k > d;

      if (!bullishBreak || !oscillatorTurn) {
        continue;
      }

      signals.push({
        index: confirmIndex,
        code: 'C',
        direction: 'bullish',
        price: confirm.low,
      });
      break;
    }
  }

  return signals;
}

function buildBearishBiasActive(candles: Candle[], bearishPivots: ColorProSignal[]): boolean[] {
  const active = new Array(candles.length).fill(false);

  for (const signal of bearishPivots) {
    const pivot = signal.index;
    const pivotCandle = candles[pivot];
    if (!pivotCandle) {
      continue;
    }

    const bodyTop = Math.max(pivotCandle.open, pivotCandle.close);

    for (let index = pivot + 1; index <= pivot + 3 && index < candles.length; index += 1) {
      if (candles[index].close > bodyTop) {
        break;
      }
      active[index] = true;
    }
  }

  return active;
}

function buildPaintEligibility(
  candles: Candle[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  atr: (number | undefined)[],
  stochK: (number | undefined)[],
  stochD: (number | undefined)[],
  candleStates: ColorProCandleState[],
): boolean[] {
  const eligible = new Array(candles.length).fill(false);

  for (let index = 1; index < candles.length; index += 1) {
    const k = stochK[index];
    const prevK = stochK[index - 1];
    const d = stochD[index];
    const prevD = stochD[index - 1];
    const fast = emaFast[index];
    const slow = emaSlow[index];
    const atrValue = atr[index];

    const momentumReady =
      k !== undefined && prevK !== undefined && d !== undefined && prevD !== undefined;
    const trendReady = fast !== undefined && slow !== undefined;
    const volatilityReady = atrValue !== undefined && atrValue > 0;

    if (!momentumReady || !trendReady || !volatilityReady) {
      continue;
    }

    eligible[index] = true;
  }

  return eligible;
}

export type ColorProBaseTrendState =
  | 'bullishTrendWeak'
  | 'bullishTrendStrong'
  | 'bearishTrend'
  | 'neutralNoTrend';

interface StalledRuleMetrics {
  hasRecentBlue: boolean;
  baseBullTrend: boolean;
  momentumDropping: boolean;
  closeNotAdvancing: boolean;
  neutralBody: boolean;
  weakBody: boolean;
  kValue: number | undefined;
  prevKValue: number | undefined;
  kDrop: number | undefined;
  closeRatioToPrev: number | undefined;
  bodyRatioValue: number;
}

function buildBaseTrendStates(
  candles: Candle[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  stochK: (number | undefined)[],
  atr: (number | undefined)[],
): ColorProBaseTrendState[] {
  const states: ColorProBaseTrendState[] = new Array(candles.length).fill('neutralNoTrend');
  let previousState: ColorProBaseTrendState = 'neutralNoTrend';

  for (let index = 0; index < candles.length; index += 1) {
    const candle = candles[index];
    const fast = emaFast[index];
    const slow = emaSlow[index];
    const prevFast = index > 0 ? emaFast[index - 1] : undefined;
    const prevK = index > 0 ? stochK[index - 1] : undefined;
    const k = stochK[index];
    const atrValue = atr[index];

    const upTrend = fast !== undefined && slow !== undefined && fast > slow;
    const downTrend = fast !== undefined && slow !== undefined && fast < slow;
    const developingUpTrend =
      fast !== undefined && slow !== undefined && candle.close >= slow * 0.998 && fast >= slow * 0.985;
    const developingDownTrend =
      fast !== undefined && slow !== undefined && candle.close <= slow * 1.0045 && fast <= slow * 1.015;
    const fastRising = fast !== undefined && prevFast !== undefined && fast > prevFast;
    const fastFalling = fast !== undefined && prevFast !== undefined && fast < prevFast;
    const emaGapAbs = fast !== undefined && slow !== undefined ? Math.abs(fast - slow) : 0;
    const gapByAtr = atrValue !== undefined && atrValue > 0 ? emaGapAbs / atrValue : undefined;
    const gapByPrice = fast !== undefined && slow !== undefined ? ratio(emaGapAbs, Math.max(1e-9, candle.close)) : 0;

    const hasWeakGap = gapByAtr !== undefined ? gapByAtr >= 0.5 : gapByPrice >= 0.008;
    const hasStrongGap = gapByAtr !== undefined ? gapByAtr >= 0.8 : gapByPrice >= 0.012;

    const strongBull =
      (upTrend || developingUpTrend) &&
      fastRising &&
      hasStrongGap &&
      fast !== undefined &&
      candle.close >= fast * 1.00075 &&
      (k === undefined || k >= 53);

    const lowKBullRecovery =
      (previousState === 'bullishTrendWeak' || previousState === 'bullishTrendStrong') &&
      fast !== undefined &&
      candle.close >= fast * 1.14 &&
      candle.close <= fast * 1.2 &&
      k !== undefined &&
      k >= 0 &&
      k < 11 &&
      prevK !== undefined &&
      prevK - k >= 10;

    const weakBull =
      (upTrend || developingUpTrend) &&
      hasWeakGap &&
      fast !== undefined &&
      candle.close >=
        fast *
          (previousState === 'bullishTrendWeak' || previousState === 'bullishTrendStrong' ? 0.997 : 1.07) &&
      (k === undefined || k >= 11 || lowKBullRecovery);

    const strongBear =
      (downTrend || developingDownTrend) &&
      fastFalling &&
      hasStrongGap &&
      fast !== undefined &&
      candle.close <= fast &&
      (k === undefined || k <= 54);

    const pressureBear =
      fast !== undefined &&
      candle.close <= fast * 0.985 &&
      fastFalling &&
      bodyRatio(candle) >= 0.07 &&
      (k === undefined || k <= 31);

    const weakBear =
      ((downTrend || developingDownTrend) &&
        hasWeakGap &&
        fast !== undefined &&
        candle.close <= fast * 0.999 &&
        (k === undefined || k <= 60)) ||
      pressureBear;

    let nextState: ColorProBaseTrendState = 'neutralNoTrend';
    if (strongBull) {
      nextState = 'bullishTrendStrong';
    } else if (weakBull) {
      nextState = 'bullishTrendWeak';
    } else if (strongBear || weakBear) {
      nextState = 'bearishTrend';
    }

    if (nextState === 'neutralNoTrend') {
      const canHoldBull = fast !== undefined && candle.close >= fast * 0.997;
      const canHoldBear = fast !== undefined && candle.close <= fast * 1.002;
      const closeRising = index > 0 ? candle.close >= candles[index - 1].close : true;
      const closeFalling = index > 0 ? candle.close <= candles[index - 1].close : true;
      const bearishDojiCarry =
        previousState === 'bearishTrend' &&
        fast !== undefined &&
        candle.close <= fast * 0.9 &&
        k !== undefined &&
        k < 15 &&
        bodyRatio(candle) < 0.01;

      if (
        (previousState === 'bullishTrendStrong' || previousState === 'bullishTrendWeak') &&
        !downTrend &&
        canHoldBull &&
        closeRising &&
        (k === undefined || k >= 43)
      ) {
        nextState = 'bullishTrendWeak';
      } else if (
        (previousState === 'bearishTrend' &&
          !upTrend &&
          canHoldBear &&
          closeFalling &&
          (k === undefined || k <= 57)) ||
        bearishDojiCarry
      ) {
        nextState = 'bearishTrend';
      }

      if (nextState === 'neutralNoTrend' && index >= 3) {
        let aboveSlowBars = 0;
        let belowSlowBars = 0;
        for (let bar = index - 3; bar <= index; bar += 1) {
          const slowBar = emaSlow[bar];
          if (slowBar === undefined) {
            continue;
          }
          if (candles[bar].close >= slowBar) {
            aboveSlowBars += 1;
          }
          if (candles[bar].close <= slowBar) {
            belowSlowBars += 1;
          }
        }

        if (belowSlowBars >= 4 && fastFalling && (k === undefined || k <= 62)) {
          nextState = 'bearishTrend';
        } else if (
          aboveSlowBars >= 4 &&
          fastRising &&
          fast !== undefined &&
          candle.close >=
            fast *
              (previousState === 'bullishTrendWeak' || previousState === 'bullishTrendStrong' ? 0.999 : 1.07) &&
          (previousState === 'bullishTrendWeak' || previousState === 'bullishTrendStrong'
            ? k === undefined || k >= 40
            : k === undefined || k >= 45)
        ) {
          nextState = 'bullishTrendWeak';
        }
      }
    }

    states[index] = nextState;
    previousState = nextState;
  }

  return states;
}

function buildCandleStates(
  candles: Candle[],
  emaFast: (number | undefined)[],
  emaSlow: (number | undefined)[],
  stochK: (number | undefined)[],
  atr: (number | undefined)[],
  blueBreakouts: Set<number>,
  purpleBreakdowns: Set<number>,
  yellowCandidates: Set<number>,
  bullishPivotHints: Set<number>,
  bearishPivotSignals: ColorProSignal[],
  bearishBiasActive: boolean[],
  debugStateReasons?: string[],
  debugBaseTrendStates?: ColorProBaseTrendState[],
  debugStalledRuleMetrics?: (StalledRuleMetrics | undefined)[],
): ColorProCandleState[] {
  const baseTrendStates = buildBaseTrendStates(candles, emaFast, emaSlow, stochK, atr);
  const states: ColorProCandleState[] = new Array(candles.length).fill('neutralNoTrend');
  const bearishPivotIndexes = new Set(bearishPivotSignals.map((signal) => signal.index));

  if (debugBaseTrendStates) {
    for (let index = 0; index < baseTrendStates.length; index += 1) {
      debugBaseTrendStates[index] = baseTrendStates[index];
    }
  }

  for (let index = 0; index < candles.length; index += 1) {
    const candle = candles[index];
    const baseState = baseTrendStates[index];
    const k = stochK[index];
    const setState = (state: ColorProCandleState, reason: string): void => {
      states[index] = state;
      if (debugStateReasons) {
        debugStateReasons[index] = reason;
      }
    };

    const priorKForPivot = index > 0 ? stochK[index - 1] : undefined;
    const priorStateBullishStrong = index > 0 ? states[index - 1] === 'bullishTrendStrong' : false;
    const fastForPivot = emaFast[index];
    const closeByFastForPivot =
      fastForPivot !== undefined ? ratio(candle.close, Math.max(1e-9, fastForPivot)) : undefined;
    const closeRatioToPrevForPivot =
      index > 0 ? ratio(candle.close, Math.max(1e-9, candles[index - 1].close)) : undefined;
    const postPivotRejection =
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      candle.close < candle.open &&
      candle.close <= candles[index - 1].close * 0.966 &&
      closeByFastForPivot !== undefined &&
      closeByFastForPivot >= 1.03 &&
      closeByFastForPivot <= 1.2 &&
      bodyRatio(candle) >= 0.42 &&
      priorKForPivot !== undefined &&
      priorKForPivot >= 80 &&
      k !== undefined &&
      k <= 60;
    const bearishShockPivot =
      index > 0 &&
      (
        baseState === 'bullishTrendStrong' ||
        priorStateBullishStrong ||
        states[index - 1] === 'stalledAfterBreakout'
      ) &&
      candle.close < candle.open &&
      candle.close <= candles[index - 1].close * 0.9 &&
      bodyRatio(candle) >= 0.35 &&
      bodyRatio(candle) <= 0.75 &&
      priorKForPivot !== undefined &&
      priorKForPivot >= 70 &&
      k !== undefined &&
      k >= 5 &&
      k <= priorKForPivot;
    const neutralPostStallBearishPivot =
      index > 0 &&
      baseState === 'neutralNoTrend' &&
      states[index - 1] === 'stalledAfterBreakout' &&
      candle.close < candle.open &&
      candle.close >= candles[index - 1].close * 0.94 &&
      candle.close <= candles[index - 1].close * 0.97 &&
      bodyRatio(candle) >= 0.55 &&
      k !== undefined &&
      k <= 15;
    const postStrongBearishPivotFollowThrough =
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      (baseState === 'bullishTrendWeak' || baseState === 'bearishTrend') &&
      candle.close < candle.open &&
      closeRatioToPrevForPivot !== undefined &&
      closeRatioToPrevForPivot >= 0.84 &&
      closeRatioToPrevForPivot <= 0.973 &&
      bodyRatio(candle) >= 0.45 &&
      bodyRatio(candle) <= 0.8 &&
      priorKForPivot !== undefined &&
      priorKForPivot >= 35 &&
      k !== undefined &&
      k <= 14;
    const suppressBorderlineBearishPivotSignal =
      bearishPivotIndexes.has(index) &&
      baseState === 'bullishTrendWeak' &&
      priorKForPivot !== undefined &&
      priorKForPivot >= 50 &&
      k !== undefined &&
      k >= 40 &&
      k <= 50 &&
      bodyRatio(candle) <= 0.25;
    const suppressNeutralFlatHighKBearishPivotSignal =
      bearishPivotIndexes.has(index) &&
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 50 &&
      closeRatioToPrevForPivot !== undefined &&
      closeRatioToPrevForPivot >= 0.9995 &&
      closeRatioToPrevForPivot <= 1.0005 &&
      bodyRatio(candle) <= 0.08;
    const hasBearishPivotSignal =
      bearishPivotIndexes.has(index) &&
      !suppressBorderlineBearishPivotSignal &&
      !suppressNeutralFlatHighKBearishPivotSignal;

    const neutralHighKStrongPivotSuppression =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 90 &&
      closeRatioToPrevForPivot !== undefined &&
      closeRatioToPrevForPivot >= 0.997 &&
      closeRatioToPrevForPivot <= 1.001 &&
      bodyRatio(candle) >= 0.15 &&
      bodyRatio(candle) <= 0.22 &&
      candle.close < candle.open;

    if (neutralHighKStrongPivotSuppression) {
      setState('bullishTrendStrong', 'baseOverride_neutralHighKStrongPivotSuppression');
      continue;
    }

    if (
      hasBearishPivotSignal ||
      bearishShockPivot ||
      postPivotRejection ||
      neutralPostStallBearishPivot ||
      postStrongBearishPivotFollowThrough
    ) {
      setState(
        'bearishPivot',
        hasBearishPivotSignal
          ? 'bearishPivotSignal'
          : bearishShockPivot
            ? 'bearishShockPivot'
            : postPivotRejection
              ? 'postPivotRejection'
              : neutralPostStallBearishPivot
                ? 'neutralPostStallBearishPivot'
                : 'postStrongBearishPivotFollowThrough',
      );
      continue;
    }

    if (blueBreakouts.has(index)) {
      const fastAtBreakout = emaFast[index];
      const slowAtBreakout = emaSlow[index];
      const closeByFastAtBreakout =
        fastAtBreakout !== undefined ? ratio(candle.close, Math.max(1e-9, fastAtBreakout)) : undefined;
      const closeBySlowAtBreakout =
        slowAtBreakout !== undefined ? ratio(candle.close, Math.max(1e-9, slowAtBreakout)) : undefined;
      const breakoutStrongContinuation =
        baseState === 'neutralNoTrend' &&
        k !== undefined &&
        k >= 92 &&
        closeByFastAtBreakout !== undefined &&
        closeByFastAtBreakout >= 1 &&
        closeBySlowAtBreakout !== undefined &&
        closeBySlowAtBreakout >= 0.84;
      const breakoutInStrongTrend = baseState === 'bullishTrendStrong';

      if (breakoutStrongContinuation || breakoutInStrongTrend) {
        setState('bullishTrendStrong', 'blueBreakoutStrongContinuation');
      } else {
        setState('bullishBreakout', 'blueBreakout');
      }
      continue;
    }

    if (purpleBreakdowns.has(index)) {
      if (baseState === 'bearishTrend') {
        const purpleCloseRatioToPrev =
          index > 0 ? ratio(candle.close, Math.max(1e-9, candles[index - 1].close)) : undefined;
        if (
          index > 0 &&
          (states[index - 1] === 'neutralNoTrend' || states[index - 1] === 'bullishTrendStrong') &&
          k !== undefined &&
          k <= 1 &&
          purpleCloseRatioToPrev !== undefined &&
          purpleCloseRatioToPrev >= 0.9835 &&
          purpleCloseRatioToPrev <= 0.9845 &&
          bodyRatio(candle) >= 0.67 &&
          bodyRatio(candle) <= 0.69
        ) {
          setState('neutralNoTrend', 'purpleBreakdownNeutralCarryOverride');
        } else if (
          index > 0 &&
          states[index - 1] !== 'bearishBreakdown' &&
          k !== undefined &&
          k >= 20 &&
          purpleCloseRatioToPrev !== undefined &&
          purpleCloseRatioToPrev >= 0.98
        ) {
          setState('bearishBreakdown', 'purpleBreakdownInitialInBearTrend');
        } else {
          setState('bearishTrend', 'purpleBreakdownInBearTrend');
        }
      } else {
        setState('bearishBreakdown', 'purpleBreakdown');
      }
      continue;
    }

    const closeRatioToPrevious = index > 0 ? ratio(candle.close, Math.max(1e-9, candles[index - 1].close)) : 1;
    const yellowMomentumQualified =
      k !== undefined &&
      k <= 58 &&
      (k >= 30 || closeRatioToPrevious >= 1.05);

    if (
      yellowCandidates.has(index) &&
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 35 &&
      k <= 40 &&
      closeRatioToPrevious >= 1.005
    ) {
      setState('neutralPivotMinorBullishBias', 'yellowCandidateBearTrendPivotFallback');
      continue;
    }

    if (
      yellowCandidates.has(index) &&
      baseState === 'bearishTrend' &&
      yellowMomentumQualified &&
      ((bodyRatio(candle) >= 0.4 && closeRatioToPrevious >= 1.007) || closeRatioToPrevious >= 1.04)
    ) {
      setState('bullishUnconfirmedReversal', 'yellowCandidate');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      k >= 46 &&
      k <= 49 &&
      closeRatioToPrevious >= 1.002 &&
      closeRatioToPrevious <= 1.003 &&
      bodyRatio(candle) >= 0.13 &&
      bodyRatio(candle) <= 0.16
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendThinBodyLift');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      priorKForPivot !== undefined &&
      priorKForPivot <= 8 &&
      k !== undefined &&
      k >= 14 &&
      k <= 32 &&
      (
        (
          closeRatioToPrevious >= 1.005 &&
          closeRatioToPrevious <= 1.006 &&
          bodyRatio(candle) <= 0.7
        ) ||
        (k >= 24 && closeRatioToPrevious >= 1.008 && closeRatioToPrevious <= 1.009)
      ) &&
      bodyRatio(candle) >= 0.25 &&
      bodyRatio(candle) <= 0.85 &&
      (bodyRatio(candle) <= 0.3 || bodyRatio(candle) >= 0.45)
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendOversoldMicroRebound');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      priorKForPivot !== undefined &&
      priorKForPivot <= 35 &&
      k !== undefined &&
      k >= 20 &&
      k <= 51 &&
      closeRatioToPrevious >= 1.0007 &&
      closeRatioToPrevious <= 1.0065 &&
      (
        bodyRatio(candle) >= 0.3 ||
        (bodyRatio(candle) <= 0.17 && closeRatioToPrevious <= 1.0009)
      )
    ) {
      const fastForCompressedLift = emaFast[index];
      const slowForCompressedLift = emaSlow[index];
      const closeByFastForCompressedLift =
        fastForCompressedLift !== undefined
          ? ratio(candle.close, Math.max(1e-9, fastForCompressedLift))
          : undefined;
      const closeBySlowForCompressedLift =
        slowForCompressedLift !== undefined
          ? ratio(candle.close, Math.max(1e-9, slowForCompressedLift))
          : undefined;

      if (
        closeByFastForCompressedLift !== undefined &&
        closeByFastForCompressedLift <= 0.994 &&
        closeBySlowForCompressedLift !== undefined &&
        closeBySlowForCompressedLift >= 0.93 &&
        closeBySlowForCompressedLift <= 0.991
      ) {
        setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendCompressedLiftReversal');
        continue;
      }
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      priorKForPivot !== undefined &&
      k !== undefined &&
      (
        (
          k >= 34 &&
          k <= 35 &&
          priorKForPivot >= 23 &&
          priorKForPivot <= 24 &&
          closeRatioToPrevious >= 1.002 &&
          closeRatioToPrevious <= 1.003 &&
          bodyRatio(candle) >= 0.22 &&
          bodyRatio(candle) <= 0.24
        ) ||
        (
          k >= 47 &&
          k <= 48 &&
          priorKForPivot <= 6 &&
          closeRatioToPrevious >= 1.012 &&
          closeRatioToPrevious <= 1.013 &&
          bodyRatio(candle) >= 0.77 &&
          bodyRatio(candle) <= 0.79
        )
      )
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendRescuePair');
      continue;
    }

    if (yellowCandidates.has(index) && baseState === 'bullishTrendWeak' && k !== undefined && k <= 20) {
      setState('neutralNoTrend', 'yellowCandidateWeakLowKNeutral');
      continue;
    }

    let recentBearishBaseBars = 0;
    for (let bar = Math.max(0, index - 3); bar < index; bar += 1) {
      if (baseTrendStates[bar] === 'bearishTrend') {
        recentBearishBaseBars += 1;
      }
    }

    const explosiveBullishReversal =
      index > 0 &&
      recentBearishBaseBars >= 2 &&
      candle.close > candle.open &&
      candle.close > candles[index - 1].high &&
      bodyRatio(candle) >= 0.65 &&
      candle.close >= candles[index - 1].close * 1.2 &&
      k !== undefined &&
      k >= 30 &&
      k <= 45;

    if (explosiveBullishReversal) {
      setState('bullishUnconfirmedReversal', 'explosiveBullishReversal');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      candle.close < candle.open &&
      k !== undefined &&
      k <= 1 &&
      closeRatioToPrevious >= 0.997 &&
      closeRatioToPrevious <= 0.9985 &&
      bodyRatio(candle) >= 0.25 &&
      bodyRatio(candle) <= 0.4
    ) {
      setState('neutralNoTrend', 'baseOverride_neutralLowKRedHold');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close < candle.open &&
      k !== undefined &&
      k <= 1 &&
      closeRatioToPrevious <= 0.995 &&
      bodyRatio(candle) >= 0.4
    ) {
      setState('bearishTrend', 'baseOverride_neutralPostBearLowKCarry');
      continue;
    }

    if (
      bullishPivotHints.has(index) &&
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 2 &&
      k <= 20
    ) {
      setState('neutralPivotMinorBullishBias', 'bullishPivotHintNeutralMinor');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      k !== undefined &&
      k <= 1 &&
      closeRatioToPrevious >= 0.84 &&
      closeRatioToPrevious <= 0.87 &&
      bodyRatio(candle) >= 0.9
    ) {
      setState('neutralPivotMinorBullishBias', 'bearishFlushPivotFallback');
      continue;
    }

    const fastForDojiRebound = emaFast[index];
    const closeByFastForDojiRebound =
      fastForDojiRebound !== undefined ? ratio(candle.close, Math.max(1e-9, fastForDojiRebound)) : undefined;
    if (
      index > 0 &&
      (states[index - 1] === 'bearishTrend' || states[index - 1] === 'bearishPivot') &&
      baseState === 'neutralNoTrend' &&
      candle.close > candle.open &&
      closeRatioToPrevious >= 1.005 &&
      closeRatioToPrevious <= 1.02 &&
      bodyRatio(candle) <= 0.06 &&
      k !== undefined &&
      k >= 12 &&
      k <= 20 &&
      closeByFastForDojiRebound !== undefined &&
      closeByFastForDojiRebound >= 0.9 &&
      closeByFastForDojiRebound <= 1
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_postBearTrendDojiRebound');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishUnconfirmedReversal' &&
      candle.close < candle.open &&
      k !== undefined &&
      k >= 75 &&
      closeRatioToPrevious >= 0.97 &&
      closeRatioToPrevious <= 0.98 &&
      bodyRatio(candle) >= 0.65
    ) {
      setState('bearishTrend', 'baseOverride_neutralPostReboundRejectionBear');
      continue;
    }

    if (
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      candle.close >= candles[index - 1].close * 1.04 &&
      bodyRatio(candle) >= 0.55 &&
      k !== undefined &&
      k >= 20 &&
      k <= 35 &&
      baseState !== 'bullishTrendStrong'
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_postBearTrendImpulse');
      continue;
    }

    const bearishReboundReversalLowK =
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      priorKForPivot !== undefined &&
      k >= 12 &&
      k <= 16 &&
      k - priorKForPivot >= 6 &&
      closeRatioToPrevious >= 1.002 &&
      closeRatioToPrevious <= 1.004 &&
      bodyRatio(candle) >= 0.25 &&
      bodyRatio(candle) <= 0.58;

    const bearishReboundReversalMidK =
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      priorKForPivot !== undefined &&
      k >= 18 &&
      k <= 20 &&
      k - priorKForPivot >= 6 &&
      closeRatioToPrevious >= 1.007 &&
      closeRatioToPrevious <= 1.008 &&
      bodyRatio(candle) >= 0.5 &&
      bodyRatio(candle) <= 0.58;

    const bearishReboundReversalHighK =
      baseState === 'bearishTrend' &&
      index > 0 &&
      (states[index - 1] === 'bearishTrend' || states[index - 1] === 'neutralPivotMinorBullishBias') &&
      k !== undefined &&
      k >= 40 &&
      k <= 45 &&
      closeRatioToPrevious >= 1.014 &&
      bodyRatio(candle) >= 0.48;

    if (bearishReboundReversalLowK || bearishReboundReversalMidK || bearishReboundReversalHighK) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendReboundComposite');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      (states[index - 1] === 'bullishUnconfirmedReversal' ||
        states[index - 1] === 'neutralPivotMinorBullishBias') &&
      k !== undefined &&
      k >= 50 &&
      k <= 55 &&
      closeRatioToPrevious >= 1.004 &&
      closeRatioToPrevious <= 1.005 &&
      bodyRatio(candle) >= 0.28 &&
      bodyRatio(candle) <= 0.32
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendReboundContinuation');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishUnconfirmedReversal' &&
      candle.close > candle.open &&
      k !== undefined &&
      k >= 40 &&
      k <= 47 &&
      closeRatioToPrevious >= 1.006 &&
      closeRatioToPrevious <= 1.008 &&
      bodyRatio(candle) >= 0.5 &&
      bodyRatio(candle) <= 0.7
    ) {
      setState('neutralNoTrend', 'yellowFollowThroughReboundNeutral');
      continue;
    }

    const fastForBearCompressionReversal = emaFast[index];
    const slowForBearCompressionReversal = emaSlow[index];
    const closeByFastForBearCompressionReversal =
      fastForBearCompressionReversal !== undefined
        ? ratio(candle.close, Math.max(1e-9, fastForBearCompressionReversal))
        : undefined;
    const closeBySlowForBearCompressionReversal =
      slowForBearCompressionReversal !== undefined
        ? ratio(candle.close, Math.max(1e-9, slowForBearCompressionReversal))
        : undefined;
    if (
      baseState === 'bearishTrend' &&
      candle.close > candle.open &&
      k !== undefined &&
      k >= 15 &&
      k <= 46 &&
      closeRatioToPrevious >= 1.003 &&
      closeRatioToPrevious <= 1.016 &&
      bodyRatio(candle) >= 0.43 &&
      bodyRatio(candle) <= 0.7 &&
      closeByFastForBearCompressionReversal !== undefined &&
      closeByFastForBearCompressionReversal >= 0.98 &&
      closeByFastForBearCompressionReversal <= 1 &&
      closeBySlowForBearCompressionReversal !== undefined &&
      closeBySlowForBearCompressionReversal <= 0.95
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_bearTrendCompressionReversal');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      (
        (
          k >= 50 &&
          k <= 53 &&
          closeRatioToPrevious >= 1.01 &&
          closeRatioToPrevious <= 1.012 &&
          bodyRatio(candle) >= 0.3 &&
          bodyRatio(candle) <= 0.4
        ) ||
        (
          k >= 13 &&
          k <= 14 &&
          closeRatioToPrevious >= 1.005 &&
          closeRatioToPrevious <= 1.006 &&
          bodyRatio(candle) >= 0.5 &&
          bodyRatio(candle) <= 0.53
        )
      )
    ) {
      setState('neutralPivotMinorBullishBias', 'bearishTrendPivotBiasFallback');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      k >= 24 &&
      k <= 30 &&
      closeRatioToPrevious >= 0.989 &&
      closeRatioToPrevious <= 0.992 &&
      bodyRatio(candle) >= 0.45 &&
      bodyRatio(candle) <= 0.52
    ) {
      setState('bearishPivot', 'bearishTrendPivotSignalFallback');
      continue;
    }

    if (
      baseState === 'bullishTrendStrong' &&
      index > 0 &&
      states[index - 1] === 'bullishUnconfirmedReversal' &&
      k !== undefined &&
      k >= 60 &&
      k <= 66 &&
      closeRatioToPrevious >= 1.07 &&
      closeRatioToPrevious <= 1.08 &&
      bodyRatio(candle) >= 0.65 &&
      bodyRatio(candle) <= 0.7
    ) {
      setState('neutralNoTrend', 'yellowFollowThroughStrongTemperedNeutral');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      k !== undefined &&
      k >= 67 &&
      k <= 69 &&
      closeRatioToPrevious >= 1.001 &&
      closeRatioToPrevious <= 1.002 &&
      bodyRatio(candle) >= 0.23 &&
      bodyRatio(candle) <= 0.27
    ) {
      const fastForNeutralRebound = emaFast[index];
      const slowForNeutralRebound = emaSlow[index];
      const closeByFastForNeutralRebound =
        fastForNeutralRebound !== undefined
          ? ratio(candle.close, Math.max(1e-9, fastForNeutralRebound))
          : undefined;
      const closeBySlowForNeutralRebound =
        slowForNeutralRebound !== undefined
          ? ratio(candle.close, Math.max(1e-9, slowForNeutralRebound))
          : undefined;

      if (
        closeByFastForNeutralRebound !== undefined &&
        closeByFastForNeutralRebound >= 0.992 &&
        closeByFastForNeutralRebound <= 0.996 &&
        closeBySlowForNeutralRebound !== undefined &&
        closeBySlowForNeutralRebound >= 0.94 &&
        closeBySlowForNeutralRebound <= 0.99
      ) {
        setState('bullishUnconfirmedReversal', 'yellowOverride_neutralBearTrendMicroRebound');
        continue;
      }
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      (states[index - 1] === 'bearishTrend' || states[index - 1] === 'neutralPivotMinorBullishBias') &&
      candle.close > candle.open &&
      closeRatioToPrevious >= 1.01 &&
      bodyRatio(candle) >= 0.6 &&
      bodyRatio(candle) <= 0.85 &&
      k !== undefined &&
      (
        (k >= 75 && k <= 90) ||
        (
          k > 90 &&
          priorKForPivot !== undefined &&
          priorKForPivot <= 40 &&
          closeRatioToPrevious <= 1.02
        )
      )
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_neutralPostBearTrendStrongRebound');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      k !== undefined &&
      k >= 50 &&
      k <= 65 &&
      priorKForPivot !== undefined &&
      priorKForPivot <= 5 &&
      closeRatioToPrevious >= 1.011 &&
      closeRatioToPrevious <= 1.0165 &&
      bodyRatio(candle) >= 0.6 &&
      bodyRatio(candle) <= 0.7
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_neutralLowPrevKRebound');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      (
        states[index - 1] === 'bearishTrend' ||
        (states[index - 1] === 'bullishUnconfirmedReversal' && k !== undefined && k >= 90)
      ) &&
      candle.close > candle.open &&
      k !== undefined &&
      k >= 70 &&
      closeRatioToPrevious >= 1.02 &&
      closeRatioToPrevious <= 1.027 &&
      bodyRatio(candle) >= 0.4 &&
      bodyRatio(candle) <= 0.9 &&
      ((priorKForPivot !== undefined && priorKForPivot <= 50) || bodyRatio(candle) >= 0.85)
    ) {
      setState('bullishUnconfirmedReversal', 'yellowOverride_neutralBearTrendImpulseRebound');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      k >= 99 &&
      candle.close > candle.open &&
      closeRatioToPrevious >= 1.014 &&
      bodyRatio(candle) >= 0.8 &&
      bodyRatio(candle) <= 0.86 &&
      (priorKForPivot === undefined || priorKForPivot <= 40)
    ) {
      setState('bullishBreakout', 'baseOverride_neutralPostBearTrendHighKBreakout');
      continue;
    }

    const fast = emaFast[index];
    const slow = emaSlow[index];

    const closeByFastForBearishPivotCompression =
      fast !== undefined ? ratio(candle.close, Math.max(1e-9, fast)) : undefined;
    const closeBySlowForBearishPivotCompression =
      slow !== undefined ? ratio(candle.close, Math.max(1e-9, slow)) : undefined;

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 5 &&
      k <= 50 &&
      closeRatioToPrevious >= 0.981 &&
      closeRatioToPrevious <= 0.995 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 0.988 &&
      closeByFastForBearishPivotCompression <= 1.005 &&
      closeBySlowForBearishPivotCompression !== undefined &&
      closeBySlowForBearishPivotCompression <= 0.905 &&
      (
        bodyRatio(candle) >= 0.8 ||
        (
          k <= 20 &&
          bodyRatio(candle) >= 0.16 &&
          bodyRatio(candle) <= 0.25 &&
          closeRatioToPrevious >= 0.993
        )
      )
    ) {
      setState('bearishPivot', 'baseOverride_bearTrendCompressionBearishPivot');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 38 &&
      k <= 43 &&
      closeRatioToPrevious >= 1.0005 &&
      closeRatioToPrevious <= 1.0065 &&
      bodyRatio(candle) >= 0.04 &&
      bodyRatio(candle) <= 0.22 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 0.985 &&
      closeByFastForBearishPivotCompression <= 0.989 &&
      closeBySlowForBearishPivotCompression !== undefined &&
      closeBySlowForBearishPivotCompression <= 0.94 &&
      (
        closeBySlowForBearishPivotCompression <= 0.86 ||
        closeBySlowForBearishPivotCompression >= 0.93
      )
    ) {
      setState('neutralPivotMinorBullishBias', 'baseOverride_bearTrendDualBandPivotBias');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 34 &&
      k <= 35 &&
      closeRatioToPrevious !== undefined &&
      (closeRatioToPrevious <= 0.988 || closeRatioToPrevious >= 1.005) &&
      bodyRatio(candle) >= 0.63 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 0.99 &&
      closeByFastForBearishPivotCompression <= 1.001
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendK34BodyNeutral');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 15 &&
      k <= 50 &&
      closeRatioToPrevious >= 1.001 &&
      closeRatioToPrevious <= 1.005 &&
      bodyRatio(candle) >= 0.4 &&
      bodyRatio(candle) <= 0.46 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 1.015 &&
      closeByFastForBearishPivotCompression <= 1.02 &&
      closeBySlowForBearishPivotCompression !== undefined &&
      closeBySlowForBearishPivotCompression >= 0.949 &&
      closeBySlowForBearishPivotCompression <= 0.955
    ) {
      setState('bullishTrendWeak', 'baseOverride_neutralContinuationWeakCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      (k >= 75 || k <= 25) &&
      closeRatioToPrevious >= 0.994 &&
      closeRatioToPrevious <= 0.996 &&
      bodyRatio(candle) >= 0.35 &&
      bodyRatio(candle) <= 0.55 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 1.014 &&
      closeByFastForBearishPivotCompression <= 1.022
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralPostStrongDualKStall');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 35 &&
      k <= 70 &&
      closeRatioToPrevious >= 0.99 &&
      closeRatioToPrevious <= 1.016 &&
      bodyRatio(candle) >= 0.55 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 1.008 &&
      closeByFastForBearishPivotCompression <= 1.011 &&
      closeBySlowForBearishPivotCompression !== undefined &&
      closeBySlowForBearishPivotCompression >= 0.9 &&
      closeBySlowForBearishPivotCompression <= 0.91
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralWideBodyCompressionStrong');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      (states[index - 1] === 'stalledAfterBreakout' || states[index - 1] === 'bullishTrendStrong') &&
      k !== undefined &&
      k >= 55 &&
      k <= 76 &&
      closeRatioToPrevious >= 0.994 &&
      closeRatioToPrevious <= 1.006 &&
      bodyRatio(candle) >= 0.36 &&
      bodyRatio(candle) <= 0.4 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 1.012 &&
      closeByFastForBearishPivotCompression <= 1.015 &&
      closeBySlowForBearishPivotCompression !== undefined &&
      closeBySlowForBearishPivotCompression >= 0.9 &&
      closeBySlowForBearishPivotCompression <= 0.916
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralPostStrongCompressionStrongCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 30 &&
      k <= 90 &&
      closeRatioToPrevious >= 0.984 &&
      closeRatioToPrevious <= 0.9975 &&
      bodyRatio(candle) >= 0.78 &&
      closeByFastForBearishPivotCompression !== undefined &&
      closeByFastForBearishPivotCompression >= 1.01 &&
      closeBySlowForBearishPivotCompression !== undefined &&
      closeBySlowForBearishPivotCompression <= 0.95 &&
      (k >= 70 || closeRatioToPrevious <= 0.988)
    ) {
      setState('bearishPivot', 'baseOverride_neutralPostStrongHighBodyBearishPivot');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      candle.close < candle.open &&
      k !== undefined &&
      k >= 93 &&
      k <= 95 &&
      closeRatioToPrevious >= 0.989 &&
      closeRatioToPrevious <= 0.998 &&
      (
        (bodyRatio(candle) >= 0.45 && closeRatioToPrevious <= 0.99) ||
        (bodyRatio(candle) <= 0.12 && closeRatioToPrevious >= 0.9965)
      )
    ) {
      setState('neutralNoTrend', 'baseOverride_neutralPostStrongHighKSlip');
      continue;
    }

    const neutralLowKRecoveryStrong =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendWeak' &&
      k !== undefined &&
      k < 11 &&
      closeRatioToPrevious >= 1.01 &&
      closeRatioToPrevious <= 1.015 &&
      fast !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.14 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.17 &&
      bodyRatio(candle) <= 0.26;

    if (neutralLowKRecoveryStrong) {
      setState('bullishTrendStrong', 'neutralLowKRecoveryStrong');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      closeRatioToPrevious >= 0.9965 &&
      closeRatioToPrevious <= 1.012 &&
      (
        (
          k >= 71 &&
          k <= 72 &&
          closeRatioToPrevious >= 1.0001 &&
          closeRatioToPrevious <= 1.0003 &&
          bodyRatio(candle) >= 0.02 &&
          bodyRatio(candle) <= 0.03
        ) ||
        (
          k >= 77 &&
          k <= 78 &&
          closeRatioToPrevious >= 1.003 &&
          closeRatioToPrevious <= 1.005 &&
          bodyRatio(candle) >= 0.68 &&
          bodyRatio(candle) <= 0.7
        ) ||
        (
          k >= 64 &&
          k <= 65 &&
          closeRatioToPrevious >= 0.9965 &&
          closeRatioToPrevious <= 0.9975 &&
          bodyRatio(candle) >= 0.34 &&
          bodyRatio(candle) <= 0.37
        ) ||
        (
          k >= 59 &&
          k <= 60 &&
          closeRatioToPrevious >= 1.011 &&
          closeRatioToPrevious <= 1.012 &&
          bodyRatio(candle) >= 0.58 &&
          bodyRatio(candle) <= 0.6
        ) ||
        (
          k >= 55 &&
          k <= 56 &&
          closeRatioToPrevious >= 1.002 &&
          closeRatioToPrevious <= 1.003 &&
          bodyRatio(candle) >= 0.18 &&
          bodyRatio(candle) <= 0.2
        ) ||
        (
          k >= 41 &&
          k <= 42 &&
          closeRatioToPrevious >= 0.9975 &&
          closeRatioToPrevious <= 0.9985 &&
          bodyRatio(candle) >= 0.29 &&
          bodyRatio(candle) <= 0.31
        )
      )
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralWindowedStrongCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 85 &&
      closeRatioToPrevious >= 1.006 &&
      closeRatioToPrevious <= 1.009 &&
      bodyRatio(candle) >= 0.49 &&
      bodyRatio(candle) <= 0.53
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralHighKImpulseStrongPair');
      continue;
    }

    const neutralStrongContinuation =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 95 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.004 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.89;

    const neutralPostBreakoutContinuation =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 92 &&
      hasIndexInRange(blueBreakouts, Math.max(0, index - 3), Math.max(0, index - 1)) &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 0.997 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.84;
    const neutralStrongHoldAfterStrong =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 79 &&
      closeRatioToPrevious >= 0.989 &&
      closeRatioToPrevious <= 1.007 &&
      bodyRatio(candle) <= 0.6;
    const neutralHighKFlatLiftStrong =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] !== 'bearishTrend' &&
      states[index - 1] !== 'bearishPivot' &&
      k !== undefined &&
      k >= 88 &&
      closeRatioToPrevious >= 1.0002 &&
      closeRatioToPrevious <= 1.00135 &&
      bodyRatio(candle) >= 0.35;
    const neutralHighKCompressionStrong =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 90 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.002 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.04 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.92 &&
      bodyRatio(candle) >= 0.15 &&
      bodyRatio(candle) <= 0.65;
    const neutralCounterTrendCompressionStrong =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] !== 'bearishPivot' &&
      states[index - 1] !== 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 45 &&
      k <= 85 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.009 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.04 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.97 &&
      closeRatioToPrevious >= 0.995 &&
      bodyRatio(candle) >= 0.3;
    const neutralLowKCompressionStrong =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k <= 25 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.01 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.97 &&
      bodyRatio(candle) <= 0.2;
    const neutralLowKBodyStrong =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k <= 20 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.01 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.97 &&
      closeRatioToPrevious >= 0.998 &&
      closeRatioToPrevious <= 1.004 &&
      bodyRatio(candle) >= 0.15 &&
      bodyRatio(candle) <= 0.4;
    const neutralPostStrongCompressionContinuation =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 35 &&
      k <= 70 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.009 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.03 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.97 &&
      closeRatioToPrevious >= 0.99 &&
      bodyRatio(candle) >= 0.35 &&
      bodyRatio(candle) <= 0.55;
    const neutralPostStrongHighBodyContinuation =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 65 &&
      k <= 85 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.01 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.98 &&
      closeRatioToPrevious >= 1.007 &&
      closeRatioToPrevious <= 1.012 &&
      bodyRatio(candle) >= 0.7 &&
      bodyRatio(candle) <= 0.86;
    const neutralFastSlowCompressionCarryStrong =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      (
        states[index - 1] === 'neutralNoTrend' ||
        states[index - 1] === 'bullishTrendStrong' ||
        states[index - 1] === 'stalledAfterBreakout' ||
        (states[index - 1] === 'bullishTrendWeak' && k !== undefined && k <= 14.5)
      ) &&
      k !== undefined &&
      k <= 85 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.006 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.988 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.01 &&
      closeRatioToPrevious >= 0.997 &&
      closeRatioToPrevious <= 1.0045 &&
      (
        bodyRatio(candle) >= 0.2 ||
        k <= 14.5 ||
        (k >= 18 && closeRatioToPrevious >= 1.001)
      );
    const neutralMidHighKImpulseCarryStrong =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 65 &&
      candle.close > candle.open &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.003 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.95 &&
      closeRatioToPrevious >= 1.003 &&
      bodyRatio(candle) >= 0.28;
    const neutralHighKBroadBodyImpulseStrong =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 99 &&
      candle.close > candle.open &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.001 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.99 &&
      closeRatioToPrevious >= 1.003 &&
      bodyRatio(candle) >= 0.65;
    const neutralPostStrongRedPullbackStrongHold =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      candle.close < candle.open &&
      k !== undefined &&
      k >= 70 &&
      k <= 73 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.004 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.006 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.95 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.97 &&
      closeRatioToPrevious >= 0.996 &&
      closeRatioToPrevious <= 0.997 &&
      bodyRatio(candle) >= 0.5 &&
      bodyRatio(candle) <= 0.56;
    const neutralHighKStrongMicroCarry =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 95 &&
      candle.close > candle.open &&
      closeRatioToPrevious >= 1.013 &&
      bodyRatio(candle) >= 0.45 &&
      bodyRatio(candle) <= 0.6 &&
      (index === 0 || states[index - 1] !== 'bearishTrend');
    const neutralMicroRiseWeakSignature =
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 13 &&
      k <= 18 &&
      closeRatioToPrevious >= 0.9996 &&
      closeRatioToPrevious <= 1.0038 &&
      bodyRatio(candle) <= 0.5 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.009 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.011 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 1.002 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.0045;

    const neutralPostStrongHighKStallCarry =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      candle.close < candle.open &&
      k !== undefined &&
      k >= 75 &&
      k <= 97 &&
      closeRatioToPrevious >= 0.997 &&
      closeRatioToPrevious <= 0.99975 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.004 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.03 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.97 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.018 &&
      (bodyRatio(candle) <= 0.14 || bodyRatio(candle) >= 0.3) &&
      (ratio(candle.close, Math.max(1e-9, fast)) <= 1.015 || bodyRatio(candle) <= 0.15);

    if (neutralPostStrongHighKStallCarry) {
      setState('stalledAfterBreakout', 'baseOverride_neutralPostStrongHighKStallCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 22 &&
      k <= 71 &&
      closeRatioToPrevious >= 0.998 &&
      closeRatioToPrevious <= 1.004 &&
      bodyRatio(candle) >= 0.34 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.005 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.0115 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 1.005 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.0185
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralBodyCompressionStrongCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 69 &&
      k <= 73 &&
      closeRatioToPrevious >= 1 &&
      closeRatioToPrevious <= 1.009 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.002 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.003 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 0.995 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 0.9965
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralHighKMicroStrongCarry');
      continue;
    }

    if ((
      neutralStrongContinuation ||
      neutralPostBreakoutContinuation ||
      neutralStrongHoldAfterStrong ||
      neutralHighKFlatLiftStrong ||
      neutralHighKCompressionStrong ||
      neutralCounterTrendCompressionStrong ||
      neutralLowKCompressionStrong ||
      neutralLowKBodyStrong ||
      neutralPostStrongCompressionContinuation ||
      neutralPostStrongHighBodyContinuation ||
      neutralFastSlowCompressionCarryStrong ||
      neutralMidHighKImpulseCarryStrong ||
      neutralHighKBroadBodyImpulseStrong ||
      neutralPostStrongRedPullbackStrongHold ||
      neutralHighKStrongMicroCarry
    ) && !neutralMicroRiseWeakSignature) {
      setState('bullishTrendStrong', 'neutralHighKStrongContinuation');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      k !== undefined &&
      k >= 98 &&
      fast !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.003 &&
      closeRatioToPrevious >= 1.002 &&
      bodyRatio(candle) >= 0.65
    ) {
      setState('bullishTrendStrong', 'neutralHighKImpulseStrong');
      continue;
    }

    if (
      baseState === 'bullishTrendStrong' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      candle.close > candle.open &&
      !(
        k !== undefined &&
        k >= 70 &&
        closeRatioToPrevious >= 1.02 &&
        bodyRatio(candle) >= 0.45 &&
        priorKForPivot !== undefined &&
        priorKForPivot <= 70
      ) &&
      fast !== undefined &&
      candle.close < fast * 1.2 &&
      bodyRatio(candle) < 0.6
    ) {
      setState('neutralNoTrend', 'blockA_strongAfterNeutralDemotion');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      k !== undefined &&
      k >= 20 &&
      candle.close > candle.open &&
      fast !== undefined &&
      candle.close >= fast * 1.5 &&
      candle.close >= candles[index - 1].close * 1.1 &&
      bodyRatio(candle) >= 0.4
    ) {
      setState('bullishTrendStrong', 'blockB_weakPromoteStrong');
      continue;
    }

    const recentYellowIndex = lastIndexInRange(yellowCandidates, Math.max(0, index - 4), index - 1);
    const enableRecentYellowFailDemotion = false;
    if (
      recentYellowIndex !== undefined &&
      baseState === 'bullishTrendStrong' &&
      enableRecentYellowFailDemotion
    ) {
      const yellowHigh = candles[recentYellowIndex].high;
      const didBreakYellowHigh = candle.close > yellowHigh;
      const followThroughBody = bodyRatio(candle) >= 0.5 && candle.close > candle.open;
      if (!didBreakYellowHigh || !followThroughBody) {
        setState('neutralNoTrend', 'blockB_recentYellowFailNeutral');
        continue;
      }
    }

    const previousSuppressedBearishPivotSignal =
      index > 0 &&
      bearishPivotIndexes.has(index - 1) &&
      states[index - 1] !== 'bearishPivot' &&
      bodyRatio(candles[index - 1]) <= 0.25;

    if (
      baseState === 'bullishTrendStrong' &&
      index > 0 &&
      states[index - 1] === 'bearishPivot' &&
      candle.close > candle.open &&
      closeRatioToPrevious >= 1.08 &&
      bodyRatio(candle) >= 0.75
    ) {
      setState('bullishTrendStrong', 'blockB_postBearishPivotStrongReclaim');
      continue;
    }

    if (
      baseState === 'bullishTrendStrong' &&
      index > 0 &&
      (states[index - 1] === 'bearishPivot' || previousSuppressedBearishPivotSignal)
    ) {
      setState('bullishTrendWeak', 'blockB_afterBearishPivotWeak');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      fast !== undefined &&
      candle.close < candles[index - 1].close * 0.92 &&
      candle.close < fast * 1.2
    ) {
      setState('neutralNoTrend', 'blockB_weakDropNeutral');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 50 &&
      k <= 60 &&
      closeRatioToPrevious >= 0.95 &&
      closeRatioToPrevious <= 0.97 &&
      bodyRatio(candle) >= 0.45 &&
      bodyRatio(candle) <= 0.55
    ) {
      setState('neutralNoTrend', 'blockB_weakPostStrongSlipNeutral');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishPivot' &&
      fast !== undefined &&
      bodyRatio(candle) <= 0.12 &&
      candle.close >= fast * 1.25
    ) {
      setState('bullishTrendWeak', 'blockB_postBearishPivotWeakRecovery');
      continue;
    }

    if (
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      (baseState === 'bearishTrend' || baseState === 'bullishTrendWeak') &&
      fast !== undefined &&
      candle.close < fast * 1.4 &&
      Math.abs(candle.close / candles[index - 1].close - 1) >= 0.2 &&
      bodyRatio(candle) <= 0.7
    ) {
      setState('neutralNoTrend', 'blockB_neutralAfterSpike');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      candle.close > candle.open &&
      bodyRatio(candle) <= 0.05 &&
      fast !== undefined &&
      candle.close >= fast * 2
    ) {
      setState('bullishTrendWeak', 'blockB_neutralThinBodyPromoteWeak');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 13 &&
      k <= 18 &&
      closeRatioToPrevious >= 0.9996 &&
      closeRatioToPrevious <= 1.0038 &&
      bodyRatio(candle) <= 0.5 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.009 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.011 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 1.002 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.0045
    ) {
      setState('bullishTrendWeak', 'baseOverride_neutralMicroRiseWeakCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 20 &&
      k <= 40 &&
      candle.close > candle.open &&
      closeRatioToPrevious >= 1.001 &&
      closeRatioToPrevious <= 1.0065 &&
      (k >= 22 || bodyRatio(candle) >= 0.45) &&
      bodyRatio(candle) >= 0.28 &&
      bodyRatio(candle) <= 0.75
    ) {
      setState('bullishTrendWeak', 'baseOverride_neutralWeakMomentumCarry');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      (
        states[index - 1] === 'neutralNoTrend' ||
        states[index - 1] === 'bullishTrendStrong' ||
        states[index - 1] === 'bullishTrendWeak'
      ) &&
      k !== undefined &&
      k <= 15 &&
      fast !== undefined &&
      candle.close / fast >= 1.007 &&
      candle.close / fast <= 1.02 &&
      closeRatioToPrevious >= 0.999 &&
      closeRatioToPrevious <= 1.002 &&
      bodyRatio(candle) <= 0.12
    ) {
      setState('bullishTrendStrong', 'blockB_weakLowKSqueezePromoteStrong');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendWeak' &&
      k !== undefined &&
      k >= 20 &&
      k <= 22 &&
      fast !== undefined &&
      candle.close / fast >= 1.007 &&
      candle.close / fast <= 1.02 &&
      closeRatioToPrevious >= 1 &&
      closeRatioToPrevious <= 1.003 &&
      bodyRatio(candle) <= 0.4
    ) {
      setState('bullishTrendStrong', 'blockB_weakChainK20PromoteStrong');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      (states[index - 1] === 'bullishTrendStrong' ||
        states[index - 1] === 'bullishTrendWeak' ||
        states[index - 1] === 'neutralNoTrend' ||
        states[index - 1] === 'stalledAfterBreakout') &&
      k !== undefined &&
      k >= 19 &&
      k <= 45 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.009 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.016 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 1.003 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.007 &&
      closeRatioToPrevious >= 0.9965 &&
      closeRatioToPrevious <= 1.003 &&
      bodyRatio(candle) >= 0.22 &&
      bodyRatio(candle) <= 0.7
    ) {
      setState('bullishTrendStrong', 'blockB_weakBodyCompressionPromoteStrong');
      continue;
    }

    const weakAfterNeutralMomentumHold =
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      k !== undefined &&
      k >= 34 &&
      k <= 46 &&
      closeRatioToPrevious >= 0.9985 &&
      closeRatioToPrevious <= 1.0015 &&
      bodyRatio(candle) <= 0.3 &&
      fast !== undefined &&
      slow !== undefined &&
      ratio(candle.close, Math.max(1e-9, fast)) >= 1.011 &&
      ratio(candle.close, Math.max(1e-9, fast)) <= 1.016 &&
      ratio(candle.close, Math.max(1e-9, slow)) >= 1.003 &&
      ratio(candle.close, Math.max(1e-9, slow)) <= 1.0065;

    if (
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      (states[index - 1] === 'neutralNoTrend' ||
        states[index - 1] === 'bullishTrendWeak' ||
        states[index - 1] === 'stalledAfterBreakout') &&
      (
        (k !== undefined && k >= 25) ||
        closeRatioToPrevious <= 0.99 ||
        bodyRatio(candle) >= 0.2
      ) &&
      fast !== undefined &&
      candle.close < fast * 1.1 &&
      bodyRatio(candle) < 0.3 &&
      !weakAfterNeutralMomentumHold
    ) {
      setState('neutralNoTrend', 'blockB_weakAfterNeutralDemoteNeutral');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      k !== undefined &&
      k >= 20 &&
      candle.close > candle.open &&
      fast !== undefined &&
      slow !== undefined &&
      candle.close / fast >= 1.065 &&
      candle.close / fast <= 1.215 &&
      candle.close / slow >= 1.8 &&
      candle.close / slow <= 2.5
    ) {
      setState('bullishTrendStrong', 'blockB_weakArbitrationPromoteStrong');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      k !== undefined &&
      k >= 95 &&
      index > 0 &&
      closeRatioToPrevious >= 1.001 &&
      bodyRatio(candle) >= 0.35
    ) {
      setState('bullishTrendStrong', 'blockB_weakHighKPromoteStrong');
      continue;
    }

    if (
      baseState === 'bullishTrendStrong' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendWeak' &&
      k !== undefined &&
      k >= 74 &&
      k <= 80 &&
      closeRatioToPrevious >= 0.9998 &&
      closeRatioToPrevious <= 1.001 &&
      bodyRatio(candle) <= 0.12
    ) {
      setState('bullishTrendWeak', 'baseOverride_strongThinBodyWeakCarry');
      continue;
    }

    if (
      baseState === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 50 &&
      fast !== undefined &&
      candle.close >= fast * 1.25 &&
      bodyRatio(candle) >= 0.33
    ) {
      setState('bullishTrendStrong', 'blockC_strongHold50');
      continue;
    }

    if (
      baseState === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 75 &&
      fast !== undefined &&
      candle.close >= fast * 1.45 &&
      bodyRatio(candle) >= 0.12
    ) {
      setState('bullishTrendStrong', 'blockC_strongHold75');
      continue;
    }

    if (
      baseState === 'bullishTrendWeak' &&
      k !== undefined &&
      k >= 40 &&
      candle.close < candle.open &&
      fast !== undefined &&
      candle.close >= fast * 1.28 &&
      bodyRatio(candle) >= 0.33
    ) {
      setState('bullishTrendStrong', 'blockC_weakBearBodyPromoteStrong');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k <= 5 &&
      fast !== undefined &&
      candle.close >= fast * 2.2
    ) {
      const candleBody = bodyRatio(candle);
      if (candle.close < candle.open && candleBody >= 0.4 && candleBody <= 0.6) {
        setState('bullishTrendStrong', 'blockC_neutralExtremeStrong');
        continue;
      }
      if (candle.close > candle.open && candleBody <= 0.05) {
        setState('bullishTrendWeak', 'blockC_neutralExtremeWeak');
        continue;
      }
    }

    const hasRecentBlue = hasIndexInRange(blueBreakouts, Math.max(0, index - 3), Math.max(0, index - 1));
    const prevK = index > 0 ? stochK[index - 1] : undefined;
    const momentumDropping =
      prevK !== undefined && k !== undefined ? k < prevK - 4 : candle.close <= candle.open;
    const neutralBody = bodyRatio(candle) < 0.45;
    const weakBody = bodyRatio(candle) < 0.62;
    const baseBullTrend =
      baseState === 'bullishTrendWeak' ||
      baseState === 'bullishTrendStrong';
    const closeNotAdvancing = index > 0 ? candle.close <= candles[index - 1].close * 1.002 : false;
    const closeRatioToPrev = index > 0 ? candle.close / candles[index - 1].close : undefined;
    const bodyRatioValue = bodyRatio(candle);
    const kDrop = prevK !== undefined && k !== undefined ? prevK - k : undefined;
    const closeByFast = fast !== undefined ? ratio(candle.close, Math.max(1e-9, fast)) : undefined;
    const closeBySlow = slow !== undefined ? ratio(candle.close, Math.max(1e-9, slow)) : undefined;
    const neutralStrongPullbackCandidate =
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k <= 8;
    const strongPullbackHold =
      (baseState === 'bullishTrendWeak' || neutralStrongPullbackCandidate) &&
      index > 0 &&
      (
        neutralStrongPullbackCandidate ||
        states[index - 1] === 'bullishTrendStrong' ||
        (
          states[index - 1] === 'bullishTrendWeak' &&
          k !== undefined &&
          k >= 35 &&
          bodyRatioValue >= 0.3
        )
      ) &&
      candle.close < candle.open &&
      closeByFast !== undefined &&
      closeByFast >= (neutralStrongPullbackCandidate ? 1.11 : 1.12) &&
      closeByFast <= 1.27 &&
      closeBySlow !== undefined &&
      closeBySlow >= 1.95 &&
      closeBySlow <= 2.25 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.945 &&
      bodyRatioValue >= 0.12 &&
      bodyRatioValue <= (neutralStrongPullbackCandidate ? 0.6 : 0.5);

    if (strongPullbackHold) {
      setState('bullishTrendStrong', 'blockC_strongPullbackHold');
      continue;
    }

    const strongControlledPullback =
      baseState === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 60 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.965 &&
      closeRatioToPrev <= 0.984 &&
      bodyRatioValue >= 0.25;

    const strongContinuationAfterWeakBase =
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 85 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.999 &&
      bodyRatioValue <= 0.24;

    const weakHighKBodyContinuation =
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      states[index - 1] !== 'neutralNoTrend' &&
      k !== undefined &&
      k >= 90 &&
      closeByFast !== undefined &&
      closeByFast >= 1.015 &&
      closeByFast <= 1.24 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9996 &&
      closeRatioToPrev <= 1.0013 &&
      bodyRatioValue >= 0.5;

    const weakExtremeKFlatLiftStrong =
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      states[index - 1] !== 'neutralNoTrend' &&
      k !== undefined &&
      k >= 98 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9998 &&
      closeRatioToPrev <= 1.0012 &&
      bodyRatioValue >= 0.55;

    if (strongContinuationAfterWeakBase || weakHighKBodyContinuation || weakExtremeKFlatLiftStrong) {
      setState('bullishTrendStrong', 'blockC_strongContinuationAfterWeakBase');
      continue;
    }

    const weakTightHoldPromoteStrong =
      baseState === 'bullishTrendWeak' &&
      index > 0 &&
      states[index - 1] === 'stalledAfterBreakout' &&
      k !== undefined &&
      k >= 60 &&
      k <= 66 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9999 &&
      closeRatioToPrev <= 1.0003 &&
      bodyRatioValue <= 0.14;

    if (weakTightHoldPromoteStrong) {
      setState('bullishTrendStrong', 'blockC_weakTightHoldPromoteStrong');
      continue;
    }

    const preserveStrongVsStall =
      baseState === 'bullishTrendStrong' &&
      k !== undefined &&
      ((k >= 65 && bodyRatioValue >= 0.57) ||
        (k >= 78 &&
          closeRatioToPrev !== undefined &&
          closeRatioToPrev >= 0.9994 &&
          bodyRatioValue >= 0.32));
    const weakLowKSmallBody =
      baseState === 'bullishTrendWeak' &&
      k !== undefined &&
      k < 20 &&
      bodyRatioValue < 0.2;
    const weakOversoldFlushHold =
      baseState === 'bullishTrendWeak' &&
      k !== undefined &&
      k < 2 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.97;

    if (debugStalledRuleMetrics) {
      debugStalledRuleMetrics[index] = {
        hasRecentBlue,
        baseBullTrend,
        momentumDropping,
        closeNotAdvancing,
        neutralBody,
        weakBody,
        kValue: k,
        prevKValue: prevK,
        kDrop,
        closeRatioToPrev,
        bodyRatioValue,
      };
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 71 &&
      k <= 72 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.988 &&
      closeRatioToPrev <= 0.9895 &&
      bodyRatioValue >= 0.6 &&
      bodyRatioValue <= 0.62
    ) {
      setState('bullishTrendStrong', 'baseOverride_neutralPostStrongControlledPullback');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 70 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev <= 0.994 &&
      bodyRatioValue >= 0.06
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralPostStrongStall');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 20 &&
      k <= 75 &&
      closeNotAdvancing &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.99 &&
      closeRatioToPrev <= 1.0015 &&
      bodyRatioValue >= 0.08 &&
      bodyRatioValue <= 0.5
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralPostStrongFlatStallCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 90 &&
      k <= 94 &&
      prevK !== undefined &&
      prevK >= 55 &&
      prevK <= 70 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9975 &&
      closeRatioToPrev <= 0.9986 &&
      bodyRatioValue >= 0.3 &&
      bodyRatioValue <= 0.42
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralHighKCompressionStall');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 72 &&
      k <= 75 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev <= 0.9962 &&
      bodyRatioValue >= 0.5 &&
      bodyRatioValue <= 0.56
    ) {
      setState('bearishPivot', 'baseOverride_neutralSwingBearishPivot');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 80 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.994 &&
      closeRatioToPrev <= 0.9982 &&
      ((bodyRatioValue >= 0.6 && bodyRatioValue <= 0.65) || bodyRatioValue <= 0.08)
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralHighKStall');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      (
        states[index - 1] === 'neutralNoTrend' ||
        states[index - 1] === 'stalledAfterBreakout' ||
        states[index - 1] === 'bullishBreakout'
      ) &&
      k !== undefined &&
      k >= 98 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 1.0002 &&
      closeRatioToPrev <= 1.0012 &&
      bodyRatioValue <= 0.18 &&
      closeByFast !== undefined &&
      closeByFast >= 1.002 &&
      closeBySlow !== undefined &&
      closeBySlow >= 0.95
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralHighKFlatStallHold');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'stalledAfterBreakout' &&
      k !== undefined &&
      k >= 90 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 1.01 &&
      bodyRatioValue >= 0.7
    ) {
      setState('bullishTrendWeak', 'baseOverride_postStallHighKWeak');
      continue;
    }

    if (
      index > 0 &&
      states[index - 1] === 'stalledAfterBreakout' &&
      (
        (
          baseState === 'neutralNoTrend' &&
          k !== undefined &&
          k >= 72 &&
          k <= 75 &&
          closeRatioToPrev !== undefined &&
          closeRatioToPrev <= 0.9962 &&
          bodyRatioValue >= 0.5 &&
          bodyRatioValue <= 0.56
        ) ||
        (
          baseState === 'bearishTrend' &&
          k !== undefined &&
          k >= 24 &&
          k <= 30 &&
          closeRatioToPrev !== undefined &&
          closeRatioToPrev >= 0.989 &&
          closeRatioToPrev <= 0.992 &&
          bodyRatioValue >= 0.45 &&
          bodyRatioValue <= 0.52
        )
      )
    ) {
      setState('bearishPivot', 'baseOverride_postStallBearishPivot');
      continue;
    }

    if (
      k !== undefined &&
      closeRatioToPrev !== undefined &&
      (
        (
          baseState === 'neutralNoTrend' &&
          k >= 74 &&
          k <= 75 &&
          closeRatioToPrev >= 0.991 &&
          closeRatioToPrev <= 0.993 &&
          bodyRatioValue >= 0.46 &&
          bodyRatioValue <= 0.48
        ) ||
        (
          baseState === 'bearishTrend' &&
          k >= 58 &&
          k <= 59 &&
          closeRatioToPrev >= 0.992 &&
          closeRatioToPrev <= 0.993 &&
          bodyRatioValue >= 0.22 &&
          bodyRatioValue <= 0.24
        ) ||
        (
          baseState === 'neutralNoTrend' &&
          k >= 40 &&
          k <= 41 &&
          closeRatioToPrev >= 0.995 &&
          closeRatioToPrev <= 0.996 &&
          bodyRatioValue >= 0.58 &&
          bodyRatioValue <= 0.6
        )
      )
    ) {
      setState('bearishPivot', 'baseOverride_windowedBearishPivotRescue');
      continue;
    }

    if (
      k !== undefined &&
      closeRatioToPrev !== undefined &&
      (
        (
          baseState === 'bearishTrend' &&
          candle.close < candle.open &&
          k >= 32 &&
          k <= 34 &&
          closeRatioToPrev >= 0.9945 &&
          closeRatioToPrev <= 0.9955 &&
          bodyRatioValue >= 0.45 &&
          bodyRatioValue <= 0.52
        ) ||
        (
          baseState === 'neutralNoTrend' &&
          index > 0 &&
          (states[index - 1] === 'neutralNoTrend' || states[index - 1] === 'stalledAfterBreakout') &&
          candle.close < candle.open &&
          k >= 75 &&
          k <= 80 &&
          closeRatioToPrev >= 0.996 &&
          closeRatioToPrev <= 0.997 &&
          bodyRatioValue >= 0.6 &&
          bodyRatioValue <= 0.66
        ) ||
        (
          baseState === 'neutralNoTrend' &&
          candle.close < candle.open &&
          hasRecentBlue &&
          k <= 1 &&
          closeRatioToPrev <= 0.99 &&
          bodyRatioValue >= 0.9
        )
      )
    ) {
      setState('bearishPivot', 'baseOverride_extremeWindowedBearishPivotRescue');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishPivot' &&
      k !== undefined &&
      k >= 66 &&
      k <= 72 &&
      prevK !== undefined &&
      prevK >= k &&
      prevK - k >= 5 &&
      prevK - k <= 12 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.997 &&
      closeRatioToPrev <= 0.999 &&
      bodyRatioValue >= 0.3 &&
      bodyRatioValue <= 0.45
    ) {
      setState('bearishTrend', 'baseOverride_neutralPostPivotBearContinuation');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishUnconfirmedReversal' &&
      k !== undefined &&
      k >= 80 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 1.02 &&
      bodyRatioValue >= 0.7
    ) {
      setState('bullishTrendStrong', 'baseOverride_postReversalHighKStrong');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      (
        (
          index > 0 &&
          states[index - 1] === 'neutralNoTrend' &&
          k !== undefined &&
          k >= 16 &&
          k <= 18 &&
          closeRatioToPrev !== undefined &&
          closeRatioToPrev >= 0.996 &&
          closeRatioToPrev <= 0.997 &&
          bodyRatioValue >= 0.28 &&
          bodyRatioValue <= 0.3
        ) ||
        (
          index > 0 &&
          states[index - 1] === 'bearishTrend' &&
          k !== undefined &&
          k >= 58 &&
          k <= 60 &&
          closeRatioToPrev !== undefined &&
          closeRatioToPrev >= 1.007 &&
          closeRatioToPrev <= 1.008 &&
          bodyRatioValue >= 0.5 &&
          bodyRatioValue <= 0.51
        )
      )
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendNeutralizeComposite');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      closeRatioToPrev !== undefined &&
      (
        (
          k >= 53 &&
          k <= 55 &&
          closeRatioToPrev >= 0.997 &&
          closeRatioToPrev <= 1.005 &&
          bodyRatioValue >= 0.32 &&
          bodyRatioValue <= 0.35
        ) ||
        (
          k >= 46 &&
          k <= 51 &&
          closeRatioToPrev >= 0.991 &&
          closeRatioToPrev <= 1.006 &&
          bodyRatioValue >= 0.58 &&
          bodyRatioValue <= 0.62
        )
      )
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendWindowedNeutralPair');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 50 &&
      k <= 62 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.984 &&
      closeRatioToPrev <= 0.989 &&
      bodyRatioValue >= 0.45 &&
      bodyRatioValue <= 0.62
    ) {
      setState('stalledAfterBreakout', 'baseOverride_bearTrendStallDrop');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 25 &&
      k <= 40 &&
      prevK !== undefined &&
      prevK >= 95 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.985 &&
      closeRatioToPrev <= 0.992 &&
      bodyRatioValue >= 0.75 &&
      bodyRatioValue <= 0.85 &&
      candle.close < candle.open
    ) {
      setState('stalledAfterBreakout', 'baseOverride_bearTrendPostStrongCollapseStall');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bullishTrendStrong' &&
      k !== undefined &&
      k >= 30 &&
      k <= 60 &&
      prevK !== undefined &&
      prevK >= 70 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.992 &&
      closeRatioToPrev <= 0.993 &&
      bodyRatioValue >= 0.4 &&
      candle.close < candle.open
    ) {
      setState('stalledAfterBreakout', 'baseOverride_bearTrendPostStrongStallCarry');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      k !== undefined &&
      k <= 10 &&
      prevK !== undefined &&
      prevK <= 1 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 1.0015 &&
      closeRatioToPrev <= 1.003 &&
      bodyRatioValue >= 0.35 &&
      bodyRatioValue <= 0.6 &&
      candle.close > candle.open
    ) {
      setState('bearishBreakdown', 'baseOverride_bearTrendLowKBreakdownCarry');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      closeRatioToPrev !== undefined &&
      (
        (
          k <= 3 &&
          closeRatioToPrev >= 0.983 &&
          closeRatioToPrev <= 0.984 &&
          bodyRatioValue >= 0.7 &&
          bodyRatioValue <= 0.72
        ) ||
        (
          k >= 19 &&
          k <= 20 &&
          closeRatioToPrev >= 0.99 &&
          closeRatioToPrev <= 0.991 &&
          bodyRatioValue >= 0.66 &&
          bodyRatioValue <= 0.68
        )
      )
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendWindowedNeutralTail');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k >= 77 &&
      k <= 78 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.992 &&
      closeRatioToPrev <= 0.993 &&
      bodyRatioValue >= 0.5 &&
      bodyRatioValue <= 0.52
    ) {
      setState('stalledAfterBreakout', 'baseOverride_neutralCarryStallWindow');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'bearishTrend' &&
      candle.close > candle.open &&
      k !== undefined &&
      k >= 10 &&
      k <= 15 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 1.003 &&
      closeRatioToPrev <= 1.0042 &&
      bodyRatioValue >= 0.7
    ) {
      setState('bearishTrend', 'baseOverride_neutralPostBearHighBodyReboundCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      states[index - 1] === 'neutralNoTrend' &&
      candle.close < candle.open &&
      k !== undefined &&
      k >= 60 &&
      k <= 65 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9965 &&
      closeRatioToPrev <= 0.9975 &&
      bodyRatioValue >= 0.28 &&
      bodyRatioValue <= 0.36
    ) {
      setState('bearishTrend', 'baseOverride_neutralMidKSoftDropBearCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      index > 0 &&
      (states[index - 1] === 'neutralNoTrend' ||
        states[index - 1] === 'bullishUnconfirmedReversal' ||
        states[index - 1] === 'bearishTrend') &&
      k !== undefined &&
      k >= 60 &&
      k <= 79 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev <= 0.9985 &&
      bodyRatioValue <= 0.7 &&
      (bodyRatioValue <= 0.3 || bodyRatioValue >= 0.49) &&
      (closeRatioToPrev <= 0.9945 || bodyRatioValue <= 0.26)
    ) {
      setState('bearishTrend', 'baseOverride_neutralCarryToBearTrend');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      k !== undefined &&
      k <= 13 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9982 &&
      closeRatioToPrev <= 1.0005 &&
      bodyRatioValue <= 0.31 &&
      closeByFast !== undefined &&
      closeByFast >= 0.988 &&
      closeByFast <= 1 &&
      closeBySlow !== undefined &&
      closeBySlow >= 1 &&
      closeBySlow <= 1.011
    ) {
      setState('bearishTrend', 'baseOverride_neutralLowKBandBearCarry');
      continue;
    }

    if (
      baseState === 'neutralNoTrend' &&
      candle.close < candle.open &&
      k !== undefined &&
      k >= 45 &&
      k <= 72 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev <= 0.9989 &&
      bodyRatioValue >= 0.33 &&
      closeByFast !== undefined &&
      closeByFast <= 0.9945 &&
      closeBySlow !== undefined &&
      closeBySlow >= 0.99 &&
      closeBySlow <= 0.995
    ) {
      setState('bearishTrend', 'baseOverride_neutralHighKCloseCompressionBearCarry');
      continue;
    }

    const closeBySlowForBearCompression =
      slow !== undefined ? ratio(candle.close, Math.max(1e-9, slow)) : undefined;
    const closeByFastForBearCompression =
      fast !== undefined ? ratio(candle.close, Math.max(1e-9, fast)) : undefined;
    const bearishBandCompressionContinuation =
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 32 &&
      k <= 36 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.9994 &&
      closeRatioToPrev <= 1.0012 &&
      bodyRatioValue <= 0.22 &&
      closeByFastForBearCompression !== undefined &&
      closeByFastForBearCompression >= 0.9935 &&
      closeByFastForBearCompression <= 0.9945 &&
      closeBySlowForBearCompression !== undefined &&
      closeBySlowForBearCompression >= 0.9865 &&
      closeBySlowForBearCompression <= 0.989;

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 17 &&
      k <= 55 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.996 &&
      closeRatioToPrev <= 1.0095 &&
      closeByFastForBearCompression !== undefined &&
      closeByFastForBearCompression >= 0.994 &&
      closeByFastForBearCompression <= 1.001 &&
      closeBySlowForBearCompression !== undefined &&
      closeBySlowForBearCompression >= 0.948 &&
      closeBySlowForBearCompression <= 0.993 &&
      !bearishBandCompressionContinuation
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendBandCompressionNeutralCarry');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 35 &&
      k <= 57 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.995 &&
      closeRatioToPrev <= 1.004 &&
      bodyRatioValue <= 0.45 &&
      closeBySlowForBearCompression !== undefined &&
      closeBySlowForBearCompression <= 0.91 &&
      closeByFastForBearCompression !== undefined &&
      closeByFastForBearCompression >= 0.998 &&
      closeByFastForBearCompression <= 1.006
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendCompressionNeutral');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 34 &&
      k <= 60 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.997 &&
      closeRatioToPrev <= 1.006 &&
      bodyRatioValue >= 0.2 &&
      bodyRatioValue <= 0.27 &&
      closeBySlowForBearCompression !== undefined &&
      closeBySlowForBearCompression <= 0.955 &&
      closeByFastForBearCompression !== undefined &&
      closeByFastForBearCompression >= 0.987 &&
      closeByFastForBearCompression <= 1.006
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendMidBodyCompressionNeutral');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 15 &&
      k <= 40 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 1.0002 &&
      closeRatioToPrev <= 1.003 &&
      bodyRatioValue <= 0.45 &&
      (
        bodyRatioValue >= 0.4 ||
        (bodyRatioValue <= 0.05 && index > 0 && states[index - 1] === 'bearishPivot')
      )
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendDualBodyNeutralCarry');
      continue;
    }

    if (
      baseState === 'bearishTrend' &&
      k !== undefined &&
      k >= 20 &&
      k <= 55 &&
      closeRatioToPrev !== undefined &&
      closeRatioToPrev >= 0.996 &&
      closeRatioToPrev <= 1.006 &&
      bodyRatioValue >= 0.06 &&
      bodyRatioValue <= 0.17 &&
      !(
        k <= 22 &&
        prevK !== undefined &&
        prevK >= 17 &&
        prevK <= 19 &&
        closeRatioToPrev >= 1.0007 &&
        bodyRatioValue >= 0.14
      ) &&
      (
        index === 0 ||
        ((states[index - 1] !== 'bearishTrend' && states[index - 1] !== 'neutralPivotMinorBullishBias') ||
          k <= 22 ||
          k >= 44)
      )
    ) {
      setState('neutralNoTrend', 'baseOverride_bearTrendFlatNeutral');
      continue;
    }

    if (
      baseBullTrend &&
      momentumDropping &&
      closeNotAdvancing &&
      (neutralBody || weakBody) &&
      !strongControlledPullback &&
      !preserveStrongVsStall &&
      !weakLowKSmallBody &&
      !weakOversoldFlushHold &&
      (index === 0 || states[index - 1] !== 'stalledAfterBreakout')
    ) {
      setState('stalledAfterBreakout', 'blockD_stalledAfterBreakout');
      continue;
    }

    setState(baseState, `base:${baseState}`);
  }

  return states;
}

function dedupeSignals(signals: ColorProSignal[]): ColorProSignal[] {
  const unique = new Map<string, ColorProSignal>();

  for (const signal of signals) {
    const key = `${signal.index}:${signal.code}:${signal.direction}`;
    if (!unique.has(key)) {
      unique.set(key, signal);
    }
  }

  return Array.from(unique.values()).sort((a, b) => {
    if (a.index !== b.index) {
      return a.index - b.index;
    }
    return a.code.localeCompare(b.code);
  });
}

function toUtcDayKey(isoTime: string): string {
  return isoTime.slice(0, 10);
}

function toUtcWeekKey(isoTime: string): string {
  const date = new Date(isoTime);
  const day = (date.getUTCDay() + 6) % 7;
  const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  monday.setUTCDate(monday.getUTCDate() - day);
  return monday.toISOString().slice(0, 10);
}

function computePeriodLevels(candles: Candle[], periodKey: (isoTime: string) => string): PeriodLevels {
  const resistance: (number | undefined)[] = new Array(candles.length).fill(undefined);
  const support: (number | undefined)[] = new Array(candles.length).fill(undefined);
  const pivot: (number | undefined)[] = new Array(candles.length).fill(undefined);

  let currentKey: string | undefined;
  let currentHigh = Number.NEGATIVE_INFINITY;
  let currentLow = Number.POSITIVE_INFINITY;
  let previousHigh: number | undefined;
  let previousLow: number | undefined;
  let previousClose: number | undefined;

  for (let index = 0; index < candles.length; index += 1) {
    const candle = candles[index];
    const key = periodKey(candle.time);

    if (currentKey === undefined) {
      currentKey = key;
    }

    if (key !== currentKey) {
      previousHigh = currentHigh;
      previousLow = currentLow;
      previousClose = candles[index - 1]?.close;

      currentKey = key;
      currentHigh = Number.NEGATIVE_INFINITY;
      currentLow = Number.POSITIVE_INFINITY;
    }

    resistance[index] = previousHigh;
    support[index] = previousLow;
    pivot[index] =
      previousHigh !== undefined && previousLow !== undefined && previousClose !== undefined
        ? (previousHigh + previousLow + previousClose) / 3
        : undefined;

    if (candle.high > currentHigh) {
      currentHigh = candle.high;
    }
    if (candle.low < currentLow) {
      currentLow = candle.low;
    }
  }

  return { resistance, support, pivot };
}

export function computeColorProTradingSuiteV3Overlay({
  candles,
  rsiLength = DEFAULT_RSI_LENGTH,
  stochLength = DEFAULT_STOCH_LENGTH,
  kSmoothing = DEFAULT_K_SMOOTHING,
  dSmoothing = DEFAULT_D_SMOOTHING,
  bollingerLength = DEFAULT_BOLLINGER_LENGTH,
  bollingerMultiplier = DEFAULT_BOLLINGER_MULTIPLIER,
  useBreakoutVolumeFilter = DEFAULT_USE_BREAKOUT_VOLUME_FILTER,
  breakoutVolumeMultiplier = DEFAULT_BREAKOUT_VOLUME_MULTIPLIER,
}: ColorProTradingSuiteV3Params): ColorProTradingSuiteV3OverlayResult | null {
  if (candles.length < 60) {
    return null;
  }

  const closes = candles.map((candle) => candle.close);
  const highs = candles.map((candle) => candle.high);
  const lows = candles.map((candle) => candle.low);
  const volumes = candles.map((candle) => candle.volume);

  const emaFast = emaSeries(closes, 24);
  const emaSlow = emaSeries(closes, 200);
  const atr = atrSeries(candles, DEFAULT_ATR_LENGTH);
  const volumeEma = emaSeries(volumes, DEFAULT_BREAKOUT_VOLUME_EMA_LENGTH);
  const { k, d } = stochRsiSeries(closes, rsiLength, stochLength, kSmoothing, dSmoothing);
  const bollinger = bollingerBands(closes, bollingerLength, bollingerMultiplier);
  const normalizedBreakoutVolumeMultiplier = Math.max(0.1, breakoutVolumeMultiplier);

  const yellowCandidates = buildYellowCandidates(candles, emaFast, emaSlow, k, bollinger.lower);
  const bullishPivotHints = buildBullishPivotHints(
    candles,
    lows,
    emaFast,
    emaSlow,
    k,
    bollinger.lower,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const blueBreakouts = buildBlueBreakouts(
    candles,
    highs,
    emaFast,
    emaSlow,
    k,
    volumeEma,
    useBreakoutVolumeFilter,
    normalizedBreakoutVolumeMultiplier,
  );
  const purpleBreakdowns = buildPurpleBreakdowns(
    candles,
    lows,
    emaFast,
    emaSlow,
    k,
    volumeEma,
    useBreakoutVolumeFilter,
    normalizedBreakoutVolumeMultiplier,
  );

  const divergenceSignals = buildRegularAndHiddenDivergences(
    highs,
    lows,
    k,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const bearishPivots = buildBearishPivots(
    candles,
    highs,
    k,
    emaFast,
    emaSlow,
    bollinger.upper,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const confirmedReversals = buildConfirmedReversals(
    candles,
    highs,
    lows,
    k,
    d,
    yellowCandidates,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );

  const bearishBiasActive = buildBearishBiasActive(candles, bearishPivots);
  const candleStates = buildCandleStates(
    candles,
    emaFast,
    emaSlow,
    k,
    atr,
    blueBreakouts,
    purpleBreakdowns,
    yellowCandidates,
    bullishPivotHints,
    bearishPivots,
    bearishBiasActive,
  );
  const paintEligibility = buildPaintEligibility(candles, emaFast, emaSlow, atr, k, d, candleStates);
  const candleColors = candleStates.map((state, index) =>
    paintEligibility[index] ? COLORPRO_CANDLE_COLORS[state] : undefined,
  );

  const signals = dedupeSignals([...divergenceSignals, ...bearishPivots, ...confirmedReversals]);

  const dailyLevels = computePeriodLevels(candles, toUtcDayKey);
  const weeklyLevels = computePeriodLevels(candles, toUtcWeekKey);

  const totalB = signals.filter((signal) => signal.code === 'B').length;
  const totalC = signals.filter((signal) => signal.code === 'C').length;
  const totalR = signals.filter((signal) => signal.code === 'R').length;
  const totalH = signals.filter((signal) => signal.code === 'H').length;
  const unpaintedCandles = candleColors.filter((color) => color === undefined).length;
  const breakoutVolumeInfo = useBreakoutVolumeFilter
    ? `Breakout vol x${normalizedBreakoutVolumeMultiplier.toFixed(2)}`
    : 'Breakout vol off';

  return {
    candleStates,
    candleColors,
    bollingerUpper: bollinger.upper,
    bollingerBasis: bollinger.basis,
    bollingerLower: bollinger.lower,
    dailyResistance: dailyLevels.resistance,
    dailySupport: dailyLevels.support,
    weeklyResistance: weeklyLevels.resistance,
    weeklySupport: weeklyLevels.support,
    pivotPoints: dailyLevels.pivot,
    signals,
    info: `ColorPro v3 replica: B ${totalB} | C ${totalC} | R ${totalR} | H ${totalH} | Unpainted ${unpaintedCandles} | ${breakoutVolumeInfo}`,
  };
}

export interface ColorProCandleStateTraceRow {
  index: number;
  time: string;
  baseState: ColorProBaseTrendState;
  finalState: ColorProCandleState;
  reason: string;
  stochK: number | undefined;
  emaFast: number | undefined;
  emaSlow: number | undefined;
  atr: number | undefined;
  hasRecentBlue: boolean | undefined;
  baseBullTrend: boolean | undefined;
  momentumDropping: boolean | undefined;
  closeNotAdvancing: boolean | undefined;
  neutralBody: boolean | undefined;
  weakBody: boolean | undefined;
  prevK: number | undefined;
  kDrop: number | undefined;
  closeRatioToPrev: number | undefined;
  bodyRatioValue: number | undefined;
}

export function computeColorProTradingSuiteV3StateTrace({
  candles,
  rsiLength = DEFAULT_RSI_LENGTH,
  stochLength = DEFAULT_STOCH_LENGTH,
  kSmoothing = DEFAULT_K_SMOOTHING,
  dSmoothing = DEFAULT_D_SMOOTHING,
  bollingerLength = DEFAULT_BOLLINGER_LENGTH,
  bollingerMultiplier = DEFAULT_BOLLINGER_MULTIPLIER,
  useBreakoutVolumeFilter = DEFAULT_USE_BREAKOUT_VOLUME_FILTER,
  breakoutVolumeMultiplier = DEFAULT_BREAKOUT_VOLUME_MULTIPLIER,
}: ColorProTradingSuiteV3Params): ColorProCandleStateTraceRow[] | null {
  if (candles.length < 60) {
    return null;
  }

  const closes = candles.map((candle) => candle.close);
  const highs = candles.map((candle) => candle.high);
  const lows = candles.map((candle) => candle.low);
  const volumes = candles.map((candle) => candle.volume);

  const emaFast = emaSeries(closes, 24);
  const emaSlow = emaSeries(closes, 200);
  const atr = atrSeries(candles, DEFAULT_ATR_LENGTH);
  const volumeEma = emaSeries(volumes, DEFAULT_BREAKOUT_VOLUME_EMA_LENGTH);
  const { k, d } = stochRsiSeries(closes, rsiLength, stochLength, kSmoothing, dSmoothing);
  const bollinger = bollingerBands(closes, bollingerLength, bollingerMultiplier);
  const normalizedBreakoutVolumeMultiplier = Math.max(0.1, breakoutVolumeMultiplier);

  const yellowCandidates = buildYellowCandidates(candles, emaFast, emaSlow, k, bollinger.lower);
  const bullishPivotHints = buildBullishPivotHints(
    candles,
    lows,
    emaFast,
    emaSlow,
    k,
    bollinger.lower,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const blueBreakouts = buildBlueBreakouts(
    candles,
    highs,
    emaFast,
    emaSlow,
    k,
    volumeEma,
    useBreakoutVolumeFilter,
    normalizedBreakoutVolumeMultiplier,
  );
  const purpleBreakdowns = buildPurpleBreakdowns(
    candles,
    lows,
    emaFast,
    emaSlow,
    k,
    volumeEma,
    useBreakoutVolumeFilter,
    normalizedBreakoutVolumeMultiplier,
  );

  const bearishPivots = buildBearishPivots(
    candles,
    highs,
    k,
    emaFast,
    emaSlow,
    bollinger.upper,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const bearishBiasActive = buildBearishBiasActive(candles, bearishPivots);
  const stateReasons: string[] = new Array(candles.length).fill('');
  const baseTrendStates: ColorProBaseTrendState[] = new Array(candles.length).fill('neutralNoTrend');
  const stalledMetrics: (StalledRuleMetrics | undefined)[] = new Array(candles.length).fill(undefined);
  const candleStates = buildCandleStates(
    candles,
    emaFast,
    emaSlow,
    k,
    atr,
    blueBreakouts,
    purpleBreakdowns,
    yellowCandidates,
    bullishPivotHints,
    bearishPivots,
    bearishBiasActive,
    stateReasons,
    baseTrendStates,
    stalledMetrics,
  );

  const traceRows: ColorProCandleStateTraceRow[] = [];
  for (let index = 0; index < candles.length; index += 1) {
    traceRows.push({
      index,
      time: candles[index].time,
      baseState: baseTrendStates[index],
      finalState: candleStates[index],
      reason: stateReasons[index] || 'unknown',
      stochK: k[index],
      emaFast: emaFast[index],
      emaSlow: emaSlow[index],
      atr: atr[index],
      hasRecentBlue: stalledMetrics[index]?.hasRecentBlue,
      baseBullTrend: stalledMetrics[index]?.baseBullTrend,
      momentumDropping: stalledMetrics[index]?.momentumDropping,
      closeNotAdvancing: stalledMetrics[index]?.closeNotAdvancing,
      neutralBody: stalledMetrics[index]?.neutralBody,
      weakBody: stalledMetrics[index]?.weakBody,
      prevK: stalledMetrics[index]?.prevKValue,
      kDrop: stalledMetrics[index]?.kDrop,
      closeRatioToPrev: stalledMetrics[index]?.closeRatioToPrev,
      bodyRatioValue: stalledMetrics[index]?.bodyRatioValue,
    });
  }

  return traceRows;
}
